/* global runtime */
// Kjernehjelpere, tilstandsnormalisering og UI<->backend-mapping (kunder, leverandører, produkter, fakturaer, kvitteringer, ansatte) samt grunnleggende posteringer.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.

/* global React, firebase, CUSTOMERS, SUPPLIERS, PRODUCTS, INVOICES, CHART, DEADLINES, VAT, TRIAL_BALANCE, RESULT, EMPLOYEES, PAY_OVERVIEW, PAY_TERMS, PAY_HOLIDAY, PAY_PENSION, PAY_RUNS, PAY_TYPES, PAY_SETTINGS */
// Backend adapter for the redesigned prototype.
// It reuses the original Plumme backend contract:
// businesses/{businessId}/app/state => { state: AppState, updatedAt, ... }

const PLUMME_FIREBASE_CONFIG = window.PLUMME_FIREBASE_CONFIG || {
  apiKey: 'AIzaSyDZKhQ7_Q2MGCERWGqR7XT30GBfxv_usJE',
  authDomain: 'zaldo-lite.firebaseapp.com',
  projectId: 'zaldo-lite',
  storageBucket: 'zaldo-lite.firebasestorage.app',
  messagingSenderId: '732286216700',
  appId: '1:732286216700:web:011033ac9e440fa9276a79',
  functionsRegion: 'europe-west1',
};
const BACKEND_EVENTS = {
  state: 'plumme-backend-state',
  products: 'plumme-products-changed',
  documentTextLines: 'plumme-document-text-lines-changed',
  drafts: 'plumme-drafts-changed',
  invoices: 'plumme-invoices-changed',
  customers: 'plumme-customers-changed',
  suppliers: 'plumme-suppliers-changed',
  projects: 'plumme-projects-changed',
  inbox: 'plumme-inbox-changed',
  mileage: 'plumme-mileage-changed',
  projects: 'plumme-projects-changed',
  employees: 'plumme-employees-changed',
  companyRoles: 'plumme-company-roles-changed',
};
const clone = (value) => JSON.parse(JSON.stringify(value || null));
const nowIso = () => new Date().toISOString();
const todayIso = () => new Date().toISOString().slice(0, 10);
const createId = (prefix = 'id') =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`);
const safeArray = (value) => (Array.isArray(value) ? value : []);
const normalizeEmail = (value) => String(value || '').trim().toLowerCase();
const isLikelyEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(value));
const LEGACY_DESIGN_BUSINESS_EMAILS = new Set(['jorgen@eopt.no']);
const isLegacyDesignBusinessEmail = (value) => LEGACY_DESIGN_BUSINESS_EMAILS.has(normalizeEmail(value));
const BUSINESS_LOGO_MAX_BYTES = 1024 * 1024;
const BUSINESS_LOGO_MIME_BY_EXTENSION = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};
const num = (value, fallback = 0) => {
  // Bevar backend-kontrakten: manglende/tom verdi er eksplisitt 0. Enkelte
  // opt-in-satser (bl.a. OTP) er avhengige av at fallback ikke aktiveres her.
  if (value == null || String(value).trim() === '') return 0;
  if (window.PlummeMoneyInput?.number) return window.PlummeMoneyInput.number(value, fallback);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const SUPPLIER_NUMBER_START = 20001;
const SUPPLIER_NUMBER_END = 29999;
const cleanPartyNumber = (value) => {
  const text = String(value || '').trim();
  return text === '-' || text === '—' ? '' : text;
};
const nextSupplierNumber = (suppliers = []) => {
  const rows = safeArray(suppliers);
  const used = new Set(rows.map((supplier) => cleanPartyNumber(supplier.supplierNumber || supplier.nr)).filter(Boolean));
  const max = rows.reduce((currentMax, supplier) => {
    const number = Number(cleanPartyNumber(supplier.supplierNumber || supplier.nr));
    return Number.isInteger(number) && number >= SUPPLIER_NUMBER_START && number <= SUPPLIER_NUMBER_END
      ? Math.max(currentMax, number)
      : currentMax;
  }, SUPPLIER_NUMBER_START - 1);
  let candidate = Math.max(SUPPLIER_NUMBER_START, max + 1);
  while (candidate <= SUPPLIER_NUMBER_END && used.has(String(candidate))) candidate += 1;
  return candidate <= SUPPLIER_NUMBER_END ? String(candidate) : '';
};
const assignSupplierNumbers = (suppliers = []) => {
  let nextRows = safeArray(suppliers);
  nextRows.forEach((supplier, index) => {
    if (cleanPartyNumber(supplier.supplierNumber)) return;
    const next = nextSupplierNumber(nextRows);
    if (!next) return;
    nextRows = nextRows.map((entry, entryIndex) => entryIndex === index ? { ...entry, supplierNumber: next } : entry);
  });
  return nextRows;
};
const rentalPricingApi = () => window.PlummeRentalPricing || globalThis.PlummeRentalPricing || null;
const normalizeRentalPricing = (pricing) => rentalPricingApi()?.normalizeRentalPricing?.(pricing) || { enabled: false, dayRates: [] };
const rentalLineBase = (line) => rentalPricingApi()?.rentalLineBase?.(line) ?? (num(line.quantity, 1) * num(line.unitPrice, 0));
const isoToNb = (iso) => {
  if (!iso || typeof iso !== 'string') return '';
  const clean = iso.slice(0, 10);
  const parts = clean.split('-');
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : iso;
};
const nbToIso = (date) => {
  if (!date) return '';
  const text = String(date).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parts = text.split('.');
  return parts.length === 3 && parts.every((part) => /^\d{1,4}$/.test(part))
    ? `${parts[2].padStart(4, '0')}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`
    : '';
};
const entryYear = (entry) => Number(nbToIso(entry?.date || entry?.createdAt).slice(0, 4));
const addDays = (iso, days) => {
  const d = new Date(nbToIso(iso));
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const stripUndefinedDeep = (value) => {
  if (Array.isArray(value)) return value.filter((entry) => entry !== undefined).map(stripUndefinedDeep);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .map(([key, entry]) => [key, stripUndefinedDeep(entry)]),
    );
  }
  return value;
};
const normalizeBankAccountNumber = (value) => String(value || '').replace(/\D/g, '');
const extractBankAccountNumberFromText = (value) => {
  const text = String(value || '');
  const candidates = [...text.matchAll(/\(([^)]*\d[^)]*)\)/g)]
    .map((match) => normalizeBankAccountNumber(match[1]))
    .filter((number) => number.length >= 7);
  return candidates.find((number) => number.length === 11) || candidates[0] || '';
};
const bankLedgerCode = (account) =>
  String(account?.ledgerAccountCode || account?.ledger || account?.accountCode || account?.code || account?.accountNumber || '').trim();
const isGeneratedLegacyBankAccount = (account) =>
  account?.id === 'settings-bank-account' ||
  (String(account?.name || '').trim().toLowerCase() === 'driftskonto' && bankLedgerCode(account) === '1920');
const chartAccountToImportedBankAccount = (account) => {
  const code = String(account?.code || account?.accountNumber || '').trim();
  if (!/^19\d{2}[:._-]/.test(code)) return null;
  const accountNumber = extractBankAccountNumberFromText(account?.name);
  if (!accountNumber) return null;
  const cleanName = String(account?.name || 'Driftskonto').replace(/\s*\([^)]*\d[^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim() || 'Driftskonto';
  return {
    id: `imported-bank-${code.replace(/[^a-z0-9_-]+/gi, '-')}`,
    name: cleanName,
    accountNumber,
    ledger: code,
    ledgerAccountCode: code,
    kind: code.startsWith('1950') ? 'tax_withholding' : 'operating',
    reconcile: true,
    showInPaymentChoices: true,
    invoiceDefault: false,
    active: true,
    importedFromChartAccount: true,
  };
};
const deriveImportedBankAccounts = (chartAccounts = []) => {
  const byNumberOrLedger = new Map();
  safeArray(chartAccounts).forEach((account) => {
    const derived = chartAccountToImportedBankAccount(account);
    if (!derived) return;
    const key = normalizeBankAccountNumber(derived.accountNumber) || derived.ledger;
    if (!byNumberOrLedger.has(key)) byNumberOrLedger.set(key, derived);
  });
  return [...byNumberOrLedger.values()];
};
const normalizeBusinessBankSettings = (settings = {}, chartAccounts = []) => {
  const importedBankAccounts = deriveImportedBankAccounts(chartAccounts);
  const importedNumbers = new Set(importedBankAccounts.map((account) => normalizeBankAccountNumber(account.accountNumber)).filter(Boolean));
  const importedLedgers = new Set(importedBankAccounts.map((account) => bankLedgerCode(account)).filter(Boolean));
  const sourceAccounts = safeArray(settings.bankAccounts);
  const normalizedSource = sourceAccounts
    .filter((account) => {
      if (!importedBankAccounts.length || !isGeneratedLegacyBankAccount(account)) return true;
      const sourceNumber = normalizeBankAccountNumber(account.accountNumber);
      const sourceLedger = bankLedgerCode(account);
      return importedNumbers.has(sourceNumber) || importedLedgers.has(sourceLedger);
    })
    .map((account) => {
      const ledger = bankLedgerCode(account) || '1920';
      return {
        ...account,
        ledger,
        ledgerAccountCode: account.ledgerAccountCode || ledger,
      };
    });
  const sourceKeys = new Set(normalizedSource.flatMap((account) => [
    normalizeBankAccountNumber(account.accountNumber),
    bankLedgerCode(account),
  ].filter(Boolean)));
  const missingImported = importedBankAccounts.filter((account) =>
    !sourceKeys.has(normalizeBankAccountNumber(account.accountNumber)) && !sourceKeys.has(bankLedgerCode(account))
  );
  const mergedAccounts = [...missingImported, ...normalizedSource];
  const hasInvoiceDefault = mergedAccounts.some((account) => account.active !== false && account.invoiceDefault);
  let assignedInvoiceDefault = false;
  const bankAccounts = mergedAccounts.map((account) => {
    if (hasInvoiceDefault || account.active === false || assignedInvoiceDefault) return account;
    assignedInvoiceDefault = true;
    return { ...account, invoiceDefault: true };
  });
  const activeAccounts = bankAccounts.filter((account) => account.active !== false);
  const currentDefault = activeAccounts.find((account) => account.invoiceDefault) || activeAccounts[0] || null;
  const normalizedBankAccount = normalizeBankAccountNumber(settings.bankAccount);
  const bankAccountMatchesActive = normalizedBankAccount && activeAccounts.some((account) => normalizeBankAccountNumber(account.accountNumber) === normalizedBankAccount);
  const bankAccount = bankAccountMatchesActive ? settings.bankAccount : (currentDefault?.accountNumber || '');
  const bankAccountingAccount = bankAccountMatchesActive
    ? (settings.bankAccountingAccount || bankLedgerCode(currentDefault) || '1920')
    : (bankLedgerCode(currentDefault) || settings.bankAccountingAccount || '1920');
  return { ...settings, bankAccounts, bankAccount, bankAccountingAccount };
};
const toIsoString = (value) => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  return undefined;
};

const normalizeAuditLog = (id, data = {}) => ({
  id,
  type: typeof data.type === 'string' ? data.type : 'business_state_updated',
  actorUserId: typeof data.actorUserId === 'string' ? data.actorUserId : undefined,
  actorEmail: typeof data.actorEmail === 'string' ? data.actorEmail : null,
  actorRole: typeof data.actorRole === 'string' ? data.actorRole : null,
  targetEmail: typeof data.targetEmail === 'string' ? data.targetEmail : null,
  targetUserId: typeof data.targetUserId === 'string' ? data.targetUserId : null,
  inviteId: typeof data.inviteId === 'string' ? data.inviteId : null,
  role: typeof data.role === 'string' ? data.role : null,
  scheduledFor: typeof data.scheduledFor === 'string' ? data.scheduledFor : toIsoString(data.scheduledFor),
  changedDomains: safeArray(data.changedDomains).filter((domain) => typeof domain === 'string'),
  changedDomainDetails: safeArray(data.changedDomainDetails)
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => ({
      domain: typeof entry.domain === 'string' ? entry.domain : '',
      beforeSummary: typeof entry.beforeSummary === 'string' ? entry.beforeSummary : null,
      afterSummary: typeof entry.afterSummary === 'string' ? entry.afterSummary : null,
      before: entry.before && typeof entry.before === 'object' ? entry.before : null,
      after: entry.after && typeof entry.after === 'object' ? entry.after : null,
    }))
    .filter((entry) => entry.domain),
  source: typeof data.source === 'string' ? data.source : null,
  createdAt: toIsoString(data.createdAt),
});

const readUiDefaultState = () => ({
  schemaVersion: 25,
  settings: {
    country: 'NO',
    businessName: 'Mitt foretak',
    organizationNumber: '',
    address: '',
    email: '',
    phone: '',
    bankAccount: '',
    bankAccounts: [],
    startInvoiceNumber: 1,
    locale: 'no',
    startYear: new Date().getFullYear(),
    vatRegistered: true,
    vatPeriod: 'bimonthly',
    standardVat: 25,
    savedInvoiceComments: [],
    // Foretakets egne dokumenter (Innstillinger -> Dokumenter). Metadata her,
    // fila i Storage under `businesses/{id}/company-documents/`.
    companyDocuments: [],
  },
  activeYear: new Date().getFullYear(),
  closedYearsByYear: {},
  budgetsByYear: {},
  // Rådgiverdata brukeren eier selv. Ligger i app-state på linje med budsjettet:
  // ingen godkjenning, ingen irreversibel handling, ingen egen samling.
  advisorGoals: [],
  plannedCashEvents: [],
  // Brukerens egne notater om hva hen har gjort med et råd. Utfører ingenting.
  advisorRecommendationStatus: {},
  // Historikk: hva rådet gjaldt da brukeren merket det gjort, og hvilket tall i
  // regnskapet som skal måles etterpå.
  advisorOutcomes: [],
  taxPrepaymentsByYear: {},
  expenses: [],
  invoices: [],
  payments: [],
  employees: [],
  // Styre og daglig leder, hentet fra Enhetsregisteret og supplert med e-post
  // brukeren fyller inn. Grunnlaget for hvem som skal signere aarsregnskapet og
  // protokollene. Se `services/companyRoles.jsx`.
  companyRoles: [],
  // Aksjeeierbok. Aksjonaerregisteret har ingen oppslagstjeneste per selskap, saa
  // dette foeres i Plumme. Trengs til generalforsamlingsprotokollen.
  shareholders: [],
  // Moetedata per aar: `{ [aar]: { board: {...}, general: {...} } }`.
  annualMeetingsByYear: {},
  payrollSettings: {
    completed: false,
    firstPayrollYear: new Date().getFullYear(),
    businessNumber: '',
    hasMultipleBusinessNumbers: false,
    holidayPayPercent: 10.2,
    holidayPayMonth: 'june',
    workSchedule: 'none',
    weeklyHours: 37.5,
    employmentType: 'ordinary',
    payrollPeriod: 'month',
    municipalityCode: '0301',
    municipalityName: 'Oslo',
    employerContributionRate: 14.1,
    calculationCode: 'general',
    useStandardEmployerContributionRate: true,
    financeTaxOnPayroll: false,
    pensionProviderName: '',
    pensionProviderOrgNumber: '',
    pensionContributionPercent: 2,
    pensionBaseAmount: 136549,
    pensionLowerG: 0,
    pensionUpperG: 12,
    customPayTypes: [],
    payrollBankAccountId: '',
    taxCardNotificationEmail: '',
    taxCardNotificationSms: '',
    birthdayReminderEnabled: false,
  },
  payrollFixedLines: [],
  payrollRuns: [],
  aMeldingSubmissions: [],
  payrollTaxPaymentsByYear: {},
  otherSales: [],
  bankStatements: [],
  bankMatchGroups: [],
  bankReconciliationByYear: {},
  bankReconciliationPostings: [],
  mileageEntries: [],
  receiptInbox: [],
  accountingImportDocuments: [],
  accountingImportLedgerRows: [],
  accountingImportOpenItemSettlements: [],
  accountingImportFixedAssets: [],
  customerSettlements: [],
  supplierSettlements: [],
  customsDeclarations: [],
  customers: [],
  customerBillingGroups: [],
  customerBillingGroupMembers: [],
  customerBillingGroupInvoiceRuns: [],
  customerBillingGroupEmailRuns: [],
  customerSubscriptions: [],
  suppliers: [],
  products: [], inventoryCountAdjustments: [],
  documentTextLines: [],
  // Prosjekter og timeføring. Timene er grunnlaget for fakturering, saa de
  // ligger i samme state-dokument som resten av regnskapet og faar samme
  // tilgangsstyring — ikke i localStorage paa klienten.
  projects: [],
  projectActivities: [],
  timeEntries: [],
  timeTrackingSettings: { lockedThrough: '', pinnedRows: [] },
  chartAccounts: [],
  // Visuelt bakgrunnstema per side. State-dokumentet er allerede isolert per
  // foretak, saa samme bruker kan ha ulike valg for ulike regnskapsklienter.
  bgByPage: {},
  deadlinesByYear: {},
  openingBalanceByYear: {},
  yearEndEntriesByYear: {},
  yearClosingBatchByYear: {},
  yearClosingProcessByYear: {},
  taxReturnProcessByYear: {},
  annualAccountsProcessByYear: {},
  norwegianPartnershipProcessByYear: {},
  norwegianCooperativeProcessByYear: {},
  norwegianAssociationFoundationProcessByYear: {},
});

const mergeState = (raw) => {
  const base = readUiDefaultState();
  const rawSettings = (raw && raw.settings) || {};
  const baseSettings = raw ? { ...base.settings, bankAccount: '' } : base.settings;
  const mergedSettings = normalizeBusinessBankSettings({ ...baseSettings, ...rawSettings }, (raw && raw.chartAccounts) || []);
  return {
    ...base,
    ...(raw || {}),
    settings: mergedSettings,
    payrollSettings: { ...base.payrollSettings, ...((raw && raw.payrollSettings) || {}) },
    suppliers: assignSupplierNumbers((raw && raw.suppliers) || base.suppliers),
  };
};

// Modellene som avledes av `runtime.appState` er rene funksjoner av den — hver
// oppdatering bytter ut hele objektet (`runtime.appState = mergeState(...)`) i stedet
// for a mutere det, sa objektidentiteten er en presis noekkel: samme objekt gir samme
// tall. Uten dette bygger radgiversiden den samme rapporten titalls ganger per render,
// og hver bygging gar gjennom alle bilag pa nytt.
const derivedModelCache = { source: undefined, day: '', entries: new Map() };
const cachedDerivedModel = (key, build) => {
  // Dagen er del av noekkelen fordi modellene bruker `new Date()` til forfall og
  // frister. En fane som star apen over midnatt skal ikke vise gardagens status.
  const day = todayIso();
  if (derivedModelCache.source !== runtime.appState || derivedModelCache.day !== day) {
    derivedModelCache.source = runtime.appState;
    derivedModelCache.day = day;
    derivedModelCache.entries = new Map();
  }
  if (derivedModelCache.entries.has(key)) return derivedModelCache.entries.get(key);
  const value = build();
  // Et tomt svar betyr som regel at appen ennaa ikke er ferdig initialisert — at
  // brukeren ikke er logget inn, eller at modellfilen ikke er lastet. Det er en
  // forbigaaende tilstand, ikke et resultat, og skal ikke laases fast i cachen.
  if (value) derivedModelCache.entries.set(key, value);
  return value;
};
// Kun for lesemodeller. Skrivestier skal fortsatt bygge sitt eget state-objekt, sa de
// aldri kan komme til a mutere instansen som ligger i cachen.
const mergedRuntimeState = () => cachedDerivedModel('mergedState', () => mergeState(runtime.appState));
// AI Regnskapsforer avleder alt fra modellene under, og maanedsvelgeren bygger
// hele modellen paa nytt ved hvert klikk. Uten cache betyr det seks fulle
// MVA-terminberegninger, to arsrapporter og en gjennomgang av alle bilag per
// maanedsknapp. Pa sma datasett merkes det ikke; etter en stor import blokkerer
// det hovedtraden pa iPad Safari. Samme argument som for radgivermodellene:
// modellene er rene funksjoner av `runtime.appState`, som byttes ut i sin helhet
// ved hver oppdatering, saa objektidentiteten er en presis noekkel.
const modelCacheKey = (name, options) => `${name}:${JSON.stringify(options || {})}`;
const getVatFilingModel = (options = {}) =>
  cachedDerivedModel(modelCacheKey('vatFiling', options), () => buildVatFilingModel(options));
const getFixedAssetsModel = (options = {}) =>
  cachedDerivedModel(modelCacheKey('fixedAssets', options), () => buildFixedAssetsModel(options));
const getTaxReturnModel = (options = {}) =>
  cachedDerivedModel(modelCacheKey('taxReturn', options), () => buildTaxReturnModel(options));
const getAnnualAccountsModel = (options = {}) =>
  cachedDerivedModel(modelCacheKey('annualAccounts', options), () => buildAnnualAccountsModel(options));
const getInvoiceListModel = (options = {}) =>
  cachedDerivedModel(modelCacheKey('invoiceList', options), () => buildInvoiceListModel(options));
const getBankPageModel = () => cachedDerivedModel('bankPage', buildBankPageModel);
const getReceiptInboxModel = () => cachedDerivedModel('receiptInbox', buildReceiptInboxModel);

const invoiceLineNet = (line) => {
  if (!invoiceBackendLineHasAmount(line)) return 0;
  const base = rentalLineBase(line);
  const discount = num(line.discountValue, 0);
  if (!discount) return base;
  const discountBasis = Math.abs(base);
  const discountAmount = line.discountType === 'amount'
    ? Math.min(discountBasis, discount)
    : Math.min(discountBasis, discountBasis * (Math.min(100, discount) / 100));
  return base - (base < 0 ? -discountAmount : discountAmount);
};
const invoiceNet = (invoice) => invoice.accountingRoundingVersion === 'NO-VAT-BUCKET-1' ? invoice.accountingAmounts.net : safeArray(invoice.lines).filter(invoiceBackendLineHasAmount).reduce((sum, line) => sum + invoiceLineNet(line), 0);
const invoiceLineVatTreatment = (line) => line.vatTreatment || (num(line.vatRate) === 0 ? 'zeroRated' : 'standard');
const invoiceVat = (invoice) => invoice.accountingRoundingVersion === 'NO-VAT-BUCKET-1' ? invoice.accountingAmounts.vat : safeArray(invoice.lines).reduce(
  (sum, line) => sum + (invoiceBackendLineHasAmount(line) && invoiceLineVatTreatment(line) === 'standard' ? invoiceLineNet(line) * num(line.vatRate, 0) / 100 : 0),
  0,
);
const invoiceGross = (invoice) => invoice.accountingRoundingVersion === 'NO-VAT-BUCKET-1' ? invoice.accountingAmounts.total : invoiceNet(invoice) + invoiceVat(invoice);

const currentCountry = () => String(runtime.appState?.settings?.country || 'NO').toUpperCase();
const countryVatLabel = (country = currentCountry()) => country === 'NO' ? 'MVA' : 'moms';
const countryVatRates = (country = currentCountry()) => {
  if (country === 'SE') return [25, 12, 6];
  if (country === 'DK') return [25];
  return [25, 15, 12];
};
const standardVatForCountry = (country = currentCountry()) => countryVatRates(country)[0] || 25;
const getSalesVatOptions = (country = currentCountry(), date = '') => {
  const label = countryVatLabel(country);
  const vatWord = t(label === 'MVA' ? 'vat.wordMva' : 'vat.wordMoms', label);
  const exemptLabel = t(label === 'MVA' ? 'vat.exemptMva' : 'vat.exemptMoms', label === 'MVA' ? 'Utenfor MVA-loven' : 'Utenfor moms');
  // Uten MVA-registrering finnes det ingen sats aa velge. «Utenfor MVA-loven» er
  // den riktige beskrivelsen av salget, ikke 0 % fritatt.
  if (!businessIsVatRegistered(date)) {
    return [{ value: 'exempt', label: exemptLabel }];
  }
  const options = countryVatRates(country).map((rate) => ({ value: String(rate), label: t('vat.rateLabel', '{{rate}} % {{word}}', { rate, word: vatWord }) }));
  options.push({ value: 'zeroRated', label: t('vat.zeroRated', '0 % {{suffix}}', { suffix: t(label === 'MVA' ? 'vat.zeroRatedSuffixMva' : 'vat.zeroRatedSuffixMoms', label === 'MVA' ? 'fritatt' : 'momsfritt') }) });
  options.push({ value: 'exempt', label: exemptLabel });
  if (country === 'NO') {
    options.push({ value: 'salesReverseCharge51', label: t('vat.salesReverseCharge51', '51 - Omvendt avgiftsplikt på salg') });
    options.push({ value: 'export52', label: t('vat.export52', '52 - Eksport/utførsel') });
  }
  return options;
};
const normalizeSalesVatOption = (value, country = currentCountry()) => {
  const raw = String(value ?? '').trim();
  if (country === 'NO' && raw === 'salesReverseCharge51') {
    return { rate: 0, treatment: 'salesReverseCharge51', reportCode: '51' };
  }
  if (country === 'NO' && raw === 'export52') {
    return { rate: 0, treatment: 'export52', reportCode: '52' };
  }
  if (raw === 'zeroRated') return { rate: 0, treatment: 'zeroRated', reportCode: country === 'NO' ? '5' : '' };
  if (raw === 'exempt') return { rate: 0, treatment: 'exempt', reportCode: '' };
  const rate = num(raw, standardVatForCountry(country));
  const allowed = countryVatRates(country);
  const normalizedRate = allowed.includes(rate) ? rate : standardVatForCountry(country);
  return { rate: normalizedRate, treatment: 'standard', reportCode: country === 'NO' ? (normalizedRate === 15 ? '31' : normalizedRate === 12 ? '33' : '3') : '' };
};
const encodeSalesVatSelection = (vatTreatment, vatRate, country = currentCountry()) => {
  if (country === 'NO' && ['salesReverseCharge51', 'export52'].includes(vatTreatment)) return vatTreatment;
  if (vatTreatment === 'zeroRated') return 'zeroRated';
  if (vatTreatment === 'exempt') return 'exempt';
  return String(num(vatRate, standardVatForCountry(country)));
};
// Et foretak uten MVA-registrering har ingen fradragsrett og skal ikke beregne
// utgaaende MVA. Forslagene tvinges alt til 0, men feltene tilbod fortsatt 25 %,
// og en manuell foring kunne dermed legge inn MVA foretaket ikke har rett paa —
// og den ville havnet i en MVA-melding som ikke skal leveres.
// Med bilagsdato gjelder registreringsperioden (MVA-registrert fra), slik serveren
// sjekker ved lagring. Uten dato gjelder bare ja/nei-valget.
const businessIsVatRegistered = (date = '') => {
  const settings = mergeState(runtime.appState)?.settings || {};
  return window.PlummeVatRegistration ? window.PlummeVatRegistration.allowsVatOn(date, settings) : settings.vatRegistered !== false;
};

const manualVatNoneLabel = (country = currentCountry()) => {
  const label = countryVatLabel(country);
  return label === 'MVA'
    ? t('vat.noVat', 'Ingen MVA')
    : t('vat.noneWithWord', 'Ingen {{word}}', { word: t('vat.wordMoms', label) });
};
const getManualVatOptions = (country = currentCountry(), date = '') => {
  if (!businessIsVatRegistered(date)) {
    return [{ value: 'none', label: manualVatNoneLabel(country) }];
  }
  if (country !== 'NO') {
    const label = countryVatLabel(country);
    const vatWord = t(label === 'MVA' ? 'vat.wordMva' : 'vat.wordMoms', label);
    return [
      { value: 'none', label: manualVatNoneLabel(country) },
      { value: 'unsupported', label: t('vat.manualUnsupported', '{{word}} på fri postering er ikke aktivert for {{country}} ennå', { word: vatWord, country }), disabled: true },
    ];
  }
  return [
    { value: 'none', label: manualVatNoneLabel(country) },
    { value: 'input-25', label: t('vat.manual.input25', '1 - Inngående MVA 25 %') },
    { value: 'input-15', label: t('vat.manual.input15', '11 - Inngående MVA 15 %') },
    { value: 'input-12', label: t('vat.manual.input12', '13 - Inngående MVA 12 %') },
    { value: 'output-25', label: t('vat.manual.output25', '3 - Utgående MVA 25 %') },
    { value: 'output-15', label: t('vat.manual.output15', '31 - Utgående MVA 15 %') },
    { value: 'output-12', label: t('vat.manual.output12', '33 - Utgående MVA 12 %') },
    { value: 'reverse-goods-25', label: t('vat.manual.reverseGoods25', '81 - Snudd avgift varer 25 %') },
    { value: 'reverse-services-25', label: t('vat.manual.reverseServices25', '86 - Snudd avgift tjenester 25 %') },
    { value: 'import-vat-only-25', label: t('vat.manual.importVatOnly25', '14 - Kun inngående MVA 25 %') },
    { value: 'import-basis-25', label: t('vat.manual.importBasis25', '21 - Grunnlag ved innførsel 25 %') },
    { value: 'import-basis-zero', label: t('vat.manual.importBasisZero', '23 - Grunnlag ved innførsel uten MVA (SAF-T 20)') },
  ];
};
// Utsending er en leveringshendelse, ikke en regnskapshendelse. Et salgsdokument som
// er ferdigstilt og har faatt fakturanummer er bokfoeringspliktig fra utstedelsen —
// utgaaende MVA og kundefordring oppstaar da, uansett om e-posten er sendt. Regelen
// stod tidligere at en usendt, ubetalt faktura var «utkast-lik», og da forsvant den
// ut av bilag, saldobalanse, resultat, MVA-melding og hjemsiden mens fakturalista
// samtidig viste den som «Ikke sendt» (altsaa opprettet, ikke utkast).
const isInvoiceDraftLike = (invoice) => {
  if (!invoice) return true;
  return invoice.status === 'draft' || invoice.status === 'utkast' || invoice.invoiceType === 'draft' || invoice.draft === true;
};
const isPostedInvoice = (invoice, state = runtime.appState) =>
  !isInvoiceDraftLike(invoice, state) &&
  invoice.kind !== 'offer' &&
  invoice.kind !== 'order_confirmation';
const isActiveOtherSale = (sale) => sale.status !== 'annulled';
const isActiveExpense = (expense) => expense.status !== 'annulled' && expense.status !== 'draft';
const isMileageExpense = (expense) =>
  Boolean(expense?.mileageEntryId) ||
  /kj(?:o|ø)rebok|yrkeskj(?:o|ø)ring|kilometergodtgj(?:o|ø)relse/i.test(expense?.description || '');
const isFreePostingExpense = (expense) =>
  Boolean(expense.manualPosting && safeArray(expense.manualPostingLines).length) ||
  /\b\d{4}\s*->\s*\d{4}\b/.test(expense.description || '');
const hasPrimaryAttachment = (entry) => Boolean(entry?.attachmentId || entry?.fileName || entry?.receiptFile || safeArray(entry?.attachments).length);
const attachmentMarkedLost = (entry) => String(entry?.attachmentStatus || '').trim() === 'missing_lost';

const uiCrmToBackend = (value) => ({
  aktiv: 'active',
  prospekt: 'prospect',
  tilbudsendt: 'offer_sent',
  pause: 'paused',
}[value] || 'active');
const backendCrmToUi = (value, status) => ({
  active: 'aktiv',
  prospect: 'prospekt',
  offer_sent: 'tilbudsendt',
  paused: 'pause',
}[value] || (status === 'archived' ? 'pause' : 'aktiv'));

const invoiceKindToUi = (kind) => ({
  offer: 'tilbud',
  credit: 'kreditnota',
  order_confirmation: 'ordre',
}[kind] || 'faktura');
const uiTypeToInvoiceKind = (type) => ({
  tilbud: 'offer',
  ordre: 'order_confirmation',
  kreditnota: 'credit',
}[type] || 'invoice');

// Betalinger og kreditnotaer slas opp per dokument, og dokumentene gas gjennom i
// loekker — betalingstid per faktura, apne poster, salgsmodellen. Med et lineaert sok
// blir det antall dokumenter ganger antall betalinger, som vokser i annen potens med
// datamengden. Indeksene henges pa selve listene, sa de forsvinner av seg selv nar
// state byttes ut og kan aldri bli utdaterte.
const EMPTY_ROWS = [];
const invoiceBackendLineType = (line) => String(line?.lineType || line?.documentLineType || 'product');
const invoiceBackendLineHasAmount = (line) => invoiceBackendLineType(line) === 'product';
const indexedBy = (cache, rows, keyOf) => {
  if (!Array.isArray(rows) || !rows.length) return null;
  const cached = cache.get(rows);
  if (cached) return cached;
  const index = new Map();
  rows.forEach((row) => {
    const key = keyOf(row);
    if (key == null) return;
    const bucket = index.get(key);
    if (bucket) bucket.push(row);
    else index.set(key, [row]);
  });
  cache.set(rows, index);
  return index;
};

const activePaymentIndexCache = new WeakMap();
const activePaymentsForDocument = (state, documentType, documentId) => {
  const index = indexedBy(
    activePaymentIndexCache,
    state?.payments,
    (payment) => payment.status === 'reversed' ? null : `${payment.documentType}:${payment.documentId}`,
  );
  return index?.get(`${documentType}:${documentId}`) || EMPTY_ROWS;
};

const creditNoteIndexCache = new WeakMap();
const creditNotesForInvoice = (state, invoiceId) => {
  const index = indexedBy(
    creditNoteIndexCache,
    state?.invoices,
    (creditInvoice) => creditInvoice.kind === 'credit' && creditInvoice.status !== 'draft'
      ? creditInvoice.creditedFromInvoiceId ?? null
      : null,
  );
  return index?.get(invoiceId) || EMPTY_ROWS;
};
const invoiceCreditedAmount = (state, invoice) => creditNotesForInvoice(state, invoice.id)
  .reduce((sum, creditInvoice) => sum + Math.abs(invoiceGross(creditInvoice)), 0);

// Hvilken fakturalinje en kreditnotalinje kom fra. Nye linjer har pekeren eksplisitt;
// eldre delkreditnotaer har den bare bakt inn i id-en `<fakturalinje>-credit-<tid>-<n>`,
// og maa leses derfra saa gammel data teller med i det som allerede er kreditert.
const creditLineSourceId = (line) => {
  const explicit = String(line?.creditedFromLineId || '').trim();
  if (explicit) return explicit;
  const id = String(line?.id || '');
  const marker = id.lastIndexOf('-credit-');
  return marker > 0 ? id.slice(0, marker) : '';
};

// Sum kreditert per fakturalinje, over alle kreditnotaer paa fakturaen. Uten dette
// kunne samme linje krediteres om igjen i hver delkreditering, og en full kreditering
// etterpaa krediterte hele fakturaen en gang til.
const invoiceCreditedByLine = (state, invoiceId) => {
  const totals = new Map();
  creditNotesForInvoice(state, invoiceId).forEach((creditNote) => {
    safeArray(creditNote.lines).forEach((line) => {
      const sourceLineId = creditLineSourceId(line);
      if (!sourceLineId) return;
      const current = totals.get(sourceLineId) || { quantity: 0, net: 0 };
      totals.set(sourceLineId, {
        quantity: current.quantity + Math.abs(num(line.quantity, 0)),
        net: current.net + Math.abs(invoiceLineNet(line)),
      });
    });
  });
  return totals;
};

const invoiceLineVatRateFor = (line) => (invoiceLineVatTreatment(line) === 'standard' ? num(line.vatRate, 0) : 0);

// Hva som faktisk kan krediteres per linje naa. Krediteringsdialogen leser denne i
// stedet for de raa fakturalinjene, slik at allerede krediterte linjer ikke kan velges
// om igjen, og «Full kreditering» bare tar resten.
const creditableInvoiceLines = (state, invoice) => {
  const credited = invoiceCreditedByLine(state, invoice.id);
  return safeArray(invoice.lines).filter(invoiceBackendLineHasAmount).map((line, index) => {
    const sourceLineId = String(line.id || `line-${index + 1}`);
    const taken = credited.get(sourceLineId) || { quantity: 0, net: 0 };
    const quantity = Math.abs(num(line.quantity, 1));
    const net = Math.abs(invoiceLineNet(line));
    const vatRate = invoiceLineVatRateFor(line);
    // Belopet er fasiten, ikke antallet. Krediterer man 400 av 1 000 kr paa en linje
    // med antall 1, er linja verken ferdig eller «1 stk brukt opp» — det staar 600 kr
    // igjen, og antallet foelger den andelen.
    const remainingNet = Math.max(0, backendRoundMoney(net - taken.net));
    const remainingQuantity = net > 0.005 ? quantity * (remainingNet / net) : 0;
    return {
      sourceLineId,
      description: line.description || line.productName || '',
      quantity: backendRoundMoney(quantity),
      unitPrice: backendRoundMoney(Math.abs(num(line.unitPrice))),
      vatRate,
      net: backendRoundMoney(net),
      gross: backendRoundMoney(net * (1 + vatRate / 100)),
      creditedQuantity: backendRoundMoney(Math.max(0, quantity - remainingQuantity)),
      creditedNet: backendRoundMoney(Math.min(net, taken.net)),
      creditedGross: backendRoundMoney(Math.min(net, taken.net) * (1 + vatRate / 100)),
      remainingQuantity: backendRoundMoney(remainingQuantity),
      remainingNet,
      remainingGross: backendRoundMoney(remainingNet * (1 + vatRate / 100)),
    };
  });
};

const activeCustomerSettlements = (state) => safeArray(state.customerSettlements)
  .filter((settlement) => settlement.status !== 'reversed');
// One pass over the settlements per state instead of one per invoice (speed track S0).
// Bucket key `${kind}:${field}:${id}`; the same array identity gives the same index.
const settlementIndexCache = new WeakMap();
const settlementsFor = (state, kind, field, id) => {
  const rows = state?.customerSettlements;
  if (!Array.isArray(rows) || !rows.length) return EMPTY_ROWS;
  let index = settlementIndexCache.get(rows);
  if (index && index.get('__length') !== rows.length) index = null;
  if (!index) {
    index = new Map([['__length', rows.length]]);
    activeCustomerSettlements(state).forEach((settlement) => {
      const add = (bucketKey) => { const bucket = index.get(bucketKey); if (bucket) bucket.push(settlement); else index.set(bucketKey, [settlement]); };
      for (const key of ['invoiceId', 'creditNoteId', 'sourceInvoiceId']) if (settlement[key]) add(`${settlement.kind}:${key}:${settlement[key]}`);
      // `either`: the settlement names the invoice on one side or the other, once per row.
      for (const id of new Set([settlement.invoiceId, settlement.sourceInvoiceId].filter(Boolean))) add(`${settlement.kind}:either:${id}`);
    });
    settlementIndexCache.set(rows, index);
  }
  return index.get(`${kind}:${field}:${id}`) || EMPTY_ROWS;
};
const settlementTotal = (rows) => rows.reduce((sum, settlement) => sum + Math.max(0, num(settlement.amount)), 0);
const allocatedCreditForInvoice = (state, invoiceId) => settlementTotal(settlementsFor(state, 'credit_offset', 'invoiceId', invoiceId));
const allocatedFromCreditNote = (state, creditNoteId) => settlementTotal(settlementsFor(state, 'credit_offset', 'creditNoteId', creditNoteId));
const allocatedOverpaymentForInvoice = (state, invoiceId) => settlementTotal(settlementsFor(state, 'overpayment_offset', 'invoiceId', invoiceId));
const allocatedFromOverpayment = (state, sourceInvoiceId) => settlementTotal(settlementsFor(state, 'overpayment_offset', 'sourceInvoiceId', sourceInvoiceId));
const customerCreditAllocationsForInvoice = (state, invoiceId) => settlementsFor(state, 'credit_offset', 'invoiceId', invoiceId)
  .map((settlement) => ({
    id: settlement.id,
    creditNoteId: settlement.creditNoteId,
    date: settlement.date || '',
    amount: backendRoundMoney(settlement.amount),
    note: settlement.note || '',
  }));
const customerOverpaymentAllocationsForInvoice = (state, invoiceId) => settlementsFor(state, 'overpayment_offset', 'either', invoiceId)
  .map((settlement) => ({
    id: settlement.id,
    sourceInvoiceId: settlement.sourceInvoiceId,
    invoiceId: settlement.invoiceId,
    sourceInvoiceNumber: invoiceById(state, settlement.sourceInvoiceId)?.invoiceNumber || settlement.sourceInvoiceId,
    invoiceNumber: invoiceById(state, settlement.invoiceId)?.invoiceNumber || settlement.invoiceId,
    date: settlement.date || '',
    amount: backendRoundMoney(settlement.amount),
    note: settlement.note || '',
  }));

const invoiceByIdCache = new WeakMap();
const invoiceById = (state, invoiceId) => {
  if (!invoiceId) return null;
  const index = indexedBy(invoiceByIdCache, state?.invoices, (entry) => entry.id ?? null);
  return index?.get(invoiceId)?.[0] || null;
};

// Dokumenter laget fra et tilbud. Indeksert, siden hvert tilbud i listen ellers
// ville skannet hele fakturalisten paa nytt.
const convertedFromCache = new WeakMap();
const documentsFromOffer = (state, offerId) => {
  if (!offerId) return [];
  const index = indexedBy(convertedFromCache, state?.invoices, (entry) => entry.convertedFromInvoiceId || null);
  return window.PlummeOfferConversion.documentsFromOffer(index?.get(offerId) || [], offerId);
};

// Flere kreditnotaer paa samme faktura tar av det aapne belopet i tur og orden, saa de
// maa ha en stabil rekkefolge uavhengig av hvordan de ligger i state.
const creditNoteOrderKey = (invoice) => `${invoice.date || ''}|${invoice.createdAt || ''}|${invoice.id || ''}`;

// En kreditnota gjor opp seg selv mot den ubetalte delen av fakturaen den krediterer:
// begge sider treffer 1500 og nuller hverandre ut, saa motregningen trenger ingen egen
// foring. Det som blir igjen er det kunden faktisk har til gode, og bare den delen kan
// betales tilbake.
const creditNoteSettlement = (state, invoice) => {
  const creditAmount = Math.abs(invoiceGross(invoice));
  const source = invoiceById(state, invoice.creditedFromInvoiceId);
  if (!source) return { creditAmount, offsetAmount: 0, refundableAmount: creditAmount, source: null };
  const sourcePaid = activePaymentsForDocument(state, 'invoice', source.id)
    .reduce((sum, payment) => sum + (payment.direction === 'outgoing' ? -1 : 1) * Math.max(0, num(payment.amount)), 0);
  let available = Math.max(0, Math.abs(invoiceGross(source)) - sourcePaid);
  let offsetAmount = 0;
  const siblings = creditNotesForInvoice(state, source.id)
    .slice()
    .sort((a, b) => creditNoteOrderKey(a).localeCompare(creditNoteOrderKey(b)));
  for (const sibling of siblings) {
    const refunded = activePaymentsForDocument(state, 'invoice', sibling.id).reduce((sum, payment) => sum + (payment.direction === 'incoming' ? -1 : 1) * Math.max(0, num(payment.amount)), 0);
    const taken = Math.min(Math.max(0, Math.abs(invoiceGross(sibling)) - refunded - allocatedFromCreditNote(state, sibling.id)), available);
    available -= taken;
    if (sibling.id === invoice.id) {
      offsetAmount = taken;
      break;
    }
  }
  const allocatedAmount = allocatedFromCreditNote(state, invoice.id);
  return { creditAmount, offsetAmount, allocatedAmount, refundableAmount: Math.max(0, creditAmount - offsetAmount - allocatedAmount), source };
};

// The summary is a pure function of the invoice, payment and settlement arrays. The list
// mapper asks for it several times per row (own row, same-customer rows, credit notes), so
// it is computed once per invoice per state (speed track S0). Each caller still gets its
// own `payments` copy, as before.
// The cache lives only while one list model is being built (withInvoiceSummaryCache), so a
// caller that edits an invoice or payment between two calls never sees a stale summary.
let activeSummaryCache = null;
const withInvoiceSummaryCache = (build) => {
  if (activeSummaryCache) return build();
  activeSummaryCache = new WeakMap();
  try { return build(); } finally { activeSummaryCache = null; }
};
const invoicePaymentSummary = (state, invoice) => {
  if (!activeSummaryCache || !invoice || typeof invoice !== 'object') return computeInvoicePaymentSummary(state, invoice);
  let byInvoice = activeSummaryCache.get(state);
  if (!byInvoice) { byInvoice = new WeakMap(); activeSummaryCache.set(state, byInvoice); }
  let summary = byInvoice.get(invoice);
  if (!summary) { summary = computeInvoicePaymentSummary(state, invoice); byInvoice.set(invoice, summary); }
  return { ...summary, payments: summary.payments.slice() };
};
const computeInvoicePaymentSummary = (state, invoice) => {
  const isCredit = invoice.kind === 'credit';
  const settlement = isCredit ? creditNoteSettlement(state, invoice) : null;
  const creditedAmount = isCredit ? 0 : invoiceCreditedAmount(state, invoice);
  const gross = invoiceGross(invoice);
  const allocatedCreditAmount = isCredit ? 0 : allocatedCreditForInvoice(state, invoice.id);
  const totalAmount = isCredit ? settlement.refundableAmount : Math.max(0, gross - creditedAmount - allocatedCreditAmount);
  const payments = activePaymentsForDocument(state, 'invoice', invoice.id);
  const expectedDirection = isCredit ? 'outgoing' : 'incoming';
  const cashPaidAmount = payments.reduce((sum, payment) => {
    const direction = payment.direction || expectedDirection;
    const amount = Math.max(0, num(payment.amount));
    return direction === expectedDirection ? sum + amount : sum - amount;
  }, 0);
  const receivedOverpaymentAmount = isCredit ? 0 : allocatedOverpaymentForInvoice(state, invoice.id);
  const paidAmount = Math.max(0, cashPaidAmount + receivedOverpaymentAmount);
  const remainingAmount = Math.max(0, totalAmount - paidAmount);
  const overpaidAmount = isCredit ? 0 : Math.max(0, cashPaidAmount - Math.abs(invoiceGross(invoice)) - allocatedFromOverpayment(state, invoice.id));
  const fullyCredited = !isCredit && gross > 0.005 && creditedAmount >= gross - 0.005;
  // Et dokument uten gjenstaende belop er gjort opp, ogsaa naar det ble null fordi alt
  // er kreditert eller motregnet. Den gamle regelen krevde totalAmount > 0 og lot
  // fullkrediterte fakturaer bli staende som ubetalte, og dermed forfalte.
  const status = remainingAmount <= 0.005 && (totalAmount > 0.005 || fullyCredited || allocatedCreditAmount > 0.005 || (isCredit && settlement.offsetAmount > 0.005))
    ? 'paid'
    : paidAmount > 0.005 ? 'partial' : 'unpaid';
  return {
    status,
    paidAmount,
    remainingAmount,
    overpaidAmount,
    totalAmount,
    creditedAmount,
    allocatedCreditAmount,
    receivedOverpaymentAmount,
    fullyCredited,
    offsetAmount: settlement?.offsetAmount || 0,
    allocatedAmount: settlement?.allocatedAmount || 0,
    refundableAmount: isCredit ? settlement.refundableAmount : 0,
    // Hvordan kreditnotaen ble gjort opp: motregnet mot fakturaen, tilbakebetalt, eller
    // fortsatt aapen som penger kunden har til gode.
    settlementMode: !isCredit
      ? null
      : settlement.refundableAmount <= 0.005 ? 'offset'
      : remainingAmount <= 0.005 ? 'refunded'
      : 'open',
    // Kopi, ikke indeksens egen liste: den som far summaryen skal kunne sortere i den
    // uten a endre oppslaget for alle andre.
    payments: payments.slice(),
  };
};

const invoiceStatusToUi = (invoice, state = runtime.appState) => {
  if (invoice.status === 'draft') return 'utkast';
  if (invoice.kind === 'credit') return 'kreditert';
  if (invoice.kind === 'offer' && invoice.offerAcceptedAt) return 'akseptert';
  if (invoice.kind === 'order_confirmation' && (invoice.orderConfirmationSentAt || invoice.sentAt)) return 'bekreftet';
  const summary = invoicePaymentSummary(state, invoice);
  // En fullkreditert faktura er ferdig, uansett om den rakk aa bli betalt forst. Uten
  // dette ble den staaende som forfalt for alltid, fordi det ikke er noe igjen aa betale.
  if (summary.fullyCredited) return 'kreditert';
  if (summary.status === 'paid') return 'betalt';
  // Et opprettet dokument er lagret og har faatt nummer — det er ikke lenger et
  // utkast bare fordi det ikke er sendt. De to ble tidligere slaatt sammen til
  // «utkast», og da forsvant nye tilbud og fakturaer ut av sin egen fane (som
  // filtrerer bort utkast) helt til brukeren sendte dem.
  if (!invoice.sentAt) return 'opprettet';
  const due = invoice.dueDate || invoice.validUntil;
  if (due && new Date(due).getTime() < Date.now() && invoice.kind !== 'offer') return 'forfalt';
  return 'sendt';
};
const uiStatusToInvoiceStatus = (status) => (status === 'utkast' ? 'draft' : 'completed');
const partyStatsKey = (value) => String(value || '').trim().toLocaleLowerCase('nb-NO');
const normalizedPartyName = (value) => String(value || '')
  .trim()
  .toLocaleLowerCase('nb-NO')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]/g, '');
const normalizedPartyOrganizationNumber = (value) => {
  const normalized = String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  return normalized.length >= 8 ? normalized : '';
};
const partiesRepresentSameEntity = (left = {}, right = {}) => {
  const leftId = String(left.id || '').trim();
  const rightId = String(right.id || '').trim();
  if (leftId && rightId && leftId === rightId) return true;
  const leftOrg = normalizedPartyOrganizationNumber(left.organizationNumber);
  const rightOrg = normalizedPartyOrganizationNumber(right.organizationNumber);
  if (leftOrg && rightOrg) return leftOrg === rightOrg;
  // E-postadressen er bevisst ikke et identitetssignal. To ulike kunder kan dele
  // en adresse - regnskapsforeren som mottar fakturaen for flere selskaper,
  // morselskapets fellespostkasse, eller et enkeltpersonforetak og personen bak
  // det. Lot vi adressen avgjore, ble den ene kunden slaatt sammen med den andre
  // i samme oyeblikk som adressen ble fylt inn, og forsvant fra kunderegisteret
  // uten at noen hadde arkivert eller slettet den.
  // Identiteten er organisasjonsnummer, kunde-/leverandornummer og navn.
  const leftNumber = String(left.customerNumber || left.supplierNumber || '').trim().toLowerCase();
  const rightNumber = String(right.customerNumber || right.supplierNumber || '').trim().toLowerCase();
  if (leftNumber && rightNumber && leftNumber === rightNumber) return true;
  const leftName = normalizedPartyName(left.name);
  return Boolean(leftName) && leftName === normalizedPartyName(right.name);
};
// Registeret viser kundene og leverandorene slik de er lagret. Ett lagret kort
// er én rad.
//
// Her laa det tidligere en automatisk sammenslaaing: to parter som lignet nok
// paa hverandre ble vist som én. Den gjorde to ting den ikke hadde lov til. Den
// avgjorde paa foretakets vegne at to kort var den samme parten — og to kunder
// kan lovlig dele navn, adresse og e-postadresse, saa likheten er ikke et bevis.
// Og den skjulte det ene kortet i samme oyeblikk som feltet ble fylt ut, uten at
// noen hadde arkivert eller slettet noe: kunden var borte fra registeret rett
// etter at brukeren lagret.
//
// To kort blir ett bare naar foretaket selv slaar dem sammen — `mergeCustomers`
// og `mergeSuppliers`, med duplikatforslagene i registeret som inngang. Skriver
// noen inn opplysninger en annen part allerede har, sier skjemaet fra der og da;
// det er en advarsel, ikke en avgjorelse.
const listPartiesForDisplay = (parties = []) =>
  safeArray(parties).filter((party) => String(party?.name || '').trim());
const accountingImportOpenItemModel = () => globalThis.PlummeAccountingImportOpenItems || {};
const importedPartyStatsFor = (state, kind, name) =>
  safeArray(state.accountingImportPartyStats?.[kind]).find((entry) => partyStatsKey(entry.partyName) === partyStatsKey(name)) || {};
const importedOpenItemsFor = (state, kind, name) =>
  safeArray(state.accountingImportOpenItems?.[kind])
    .filter((entry) => partyStatsKey(entry.partyName) === partyStatsKey(name))
    .map((entry) => accountingImportOpenItemModel().decorate?.(state, entry) || entry)
    .sort((a, b) => String(a.dueDate || a.date || '').localeCompare(String(b.dueDate || b.date || '')));
const importedSettledAmountFor = (state, kind, name) => importedOpenItemsFor(state, kind, name)
  .reduce((sum, item) => sum + Math.max(0, num(item.originalRemainingAmount) - Math.max(0, num(item.remainingAmount))), 0);
const importedOutstandingFor = (state, statsKind, openItemsKind, name) => {
  const stats = importedPartyStatsFor(state, statsKind, name);
  const items = importedOpenItemsFor(state, openItemsKind, name);
  const detailTotal = items.reduce((sum, item) => sum + num(item.remainingAmount), 0);
  if (!Object.keys(stats).length) return Math.max(0, detailTotal);
  return Math.max(0, num(stats.outstanding) - importedSettledAmountFor(state, openItemsKind, name));
};
const importedNetOutstandingFor = (state, statsKind, openItemsKind, name) => {
  const stats = importedPartyStatsFor(state, statsKind, name);
  const items = importedOpenItemsFor(state, openItemsKind, name);
  if (Object.keys(stats).length) return num(stats.outstanding) - importedSettledAmountFor(state, openItemsKind, name);
  return items.reduce((sum, item) => sum + num(item.remainingAmount), 0);
};
const customerInvoiceOpenBalance = (state, invoice) => {
  if (!invoice || invoice.status === 'draft' || invoice.kind === 'offer' || invoice.kind === 'order_confirmation') return 0;
  const gross = Math.abs(invoiceGross(invoice));
  const payments = activePaymentsForDocument(state, 'invoice', invoice.id);
  if (invoice.kind === 'credit') {
    const refunded = payments.reduce((sum, payment) =>
      (payment.direction || 'outgoing') === 'outgoing' ? sum + Math.max(0, num(payment.amount)) : sum, 0);
    return Math.min(0, -gross + refunded + allocatedFromCreditNote(state, invoice.id));
  }
  const paidAmount = payments.length
    ? payments.reduce((sum, payment) => {
      const amount = Math.max(0, num(payment.amount));
      return (payment.direction || 'incoming') === 'incoming' ? sum + amount : sum - amount;
    }, 0)
    : invoice.paidAt ? gross : 0;
  return gross - paidAmount - allocatedCreditForInvoice(state, invoice.id) - allocatedOverpaymentForInvoice(state, invoice.id) + allocatedFromOverpayment(state, invoice.id);
};

const mapCustomerToUi = (customer, state) => {
  const invoices = safeArray(state.invoices).filter((invoice) => partiesRepresentSameEntity(customer, {
    ...(invoice.customer || {}),
    id: invoice.customer?.id || invoice.customerId,
    organizationNumber: invoice.customer?.organizationNumber || invoice.customerOrganizationNumber,
  }));
  const gross = invoices.filter((invoice) => isPostedInvoice(invoice, state) && invoice.status !== 'annulled').reduce((sum, invoice) => sum + (invoice.kind === 'credit' ? -Math.abs(invoiceGross(invoice)) : invoiceGross(invoice)), 0);
  // Utestaende er det som faktisk gjenstaar, ikke bruttobelopet paa alt uten paidAt: en
  // fullkreditert eller delbetalt faktura skal ikke ligge og lyse som ubetalt, og en
  // kreditnota kunden ikke har faatt pengene for er noe kunden har til gode av oss.
  const open = invoices
    .reduce((sum, invoice) => sum + customerInvoiceOpenBalance(state, invoice), 0);
  const importedStats = importedPartyStatsFor(state, 'customers', customer.name);
  const importedOpenItems = importedOpenItemsFor(state, 'customerInvoices', customer.name);
  return {
    id: customer.id,
    nr: customer.customerNumber || customer.memberNumber || '',
    name: customer.name || '',
    org: customer.organizationNumber || '',
    email: customer.email || '',
    phone: customer.phone || '',
    addr: [customer.address, customer.postalCode, customer.postalPlace].filter(Boolean).join(', '),
    zip: customer.postalCode || '',
    city: customer.postalPlace || '',
    out: Math.round(open + importedNetOutstandingFor(state, 'customers', 'customerInvoices', customer.name)),
    sales: Math.round(gross + num(importedStats.totalSales)),
    importedOpenItems,
    status: customer.status === 'archived' ? 'passiv' : 'aktiv',
    arkivert: customer.status === 'archived',
    crm: backendCrmToUi(customer.crmStatus, customer.status),
    followUp: customer.nextFollowUpDate || '',
    defaultCustomerReference: customer.defaultCustomerReference || '', creditLimit: customer.creditLimit === null || customer.creditLimit === undefined || customer.creditLimit === '' || !Number.isFinite(Number(customer.creditLimit)) ? '' : Number(customer.creditLimit),
    note: customer.note || '',
    contactPersons: safeArray(customer.contactPersons),
    activities: safeArray(customer.crmActivities).map((activity) => ({
      date: (activity.createdAt || '').slice(0, 10),
      text: activity.text || '',
    })),
    conversationThreads: safeArray(customer.conversationThreads),
    _backend: customer,
  };
};

const mapUiCustomerToBackend = (customer, existing = {}) => {
  const addressParts = String(customer.addr || existing.address || '').split(',').map((part) => part.trim());
  return {
    ...existing,
    id: customer.id || existing.id || `c${Date.now()}`,
    name: customer.name || existing.name || 'Uten navn',
    customerNumber: customer.nr || existing.customerNumber || '',
    organizationNumber: customer.org === '-' ? '' : (customer.org || existing.organizationNumber || ''),
    email: customer.email || existing.email || '',
    phone: customer.phone || existing.phone || '',
    address: addressParts[0] || existing.address || '',
    postalCode: customer.zip || customer.postalCode || existing.postalCode || '',
    postalPlace: customer.city || customer.postalPlace || addressParts.slice(1).join(', ') || existing.postalPlace || '',
    createdAt: existing.createdAt || nowIso(),
    status: customer.arkivert || customer.status === 'passiv' ? 'archived' : 'active',
    archivedAt: customer.arkivert ? (existing.archivedAt || nowIso()) : undefined,
    crmStatus: uiCrmToBackend(customer.crm),
    nextFollowUpDate: customer.followUp || existing.nextFollowUpDate || '',
    defaultCustomerReference: customer.defaultCustomerReference === '-' ? '' : (customer.defaultCustomerReference || existing.defaultCustomerReference || ''), creditLimit: customer.creditLimit === '' || customer.creditLimit === null || customer.creditLimit === undefined ? null : (Number.isFinite(Number(customer.creditLimit)) ? Number(customer.creditLimit) : (existing.creditLimit ?? null)),
    note: customer.note || existing.note || '',
    contactPersons: safeArray(customer.contactPersons || existing.contactPersons)
      .map((contact, index) => stripUndefinedDeep({
        id: contact.id || `contact-${index}-${Date.now()}`,
        name: contact.name || contact.navn || '',
        role: contact.role || contact.rolle || '',
        email: contact.email || contact.epost || '',
        phone: contact.phone || contact.telefon || '',
      }))
      .filter((contact) => contact.name || contact.email || contact.phone),
    crmActivities: safeArray(customer.activities).map((activity, index) => ({
      id: activity.id || `act${index}`,
      type: 'note',
      createdAt: activity.date || nowIso(),
      text: activity.text || '',
    })),
    conversationThreads: safeArray(customer.conversationThreads || existing.conversationThreads),
  };
};

const mapSupplierToUi = (supplier, state) => { const accountingCountry = state?.settings?.country || currentCountry(), country = window.PlummeSupplierRecords.supplierCountry(supplier, accountingCountry), foreign = window.PlummeSupplierRecords.isForeign({ country }, accountingCountry);
  const purchases = safeArray(state.expenses).filter((expense) =>
    expense.supplierId ? expense.supplierId === supplier.id : expense.supplierName === supplier.name,
  );
  const buy = safeArray(state.expenses).filter(expense=>!['draft','annulled','deleted','reversed'].includes(expense.status)).reduce((sum,expense)=>{const original=expense.supplierCreditForExpenseId?safeArray(state.expenses).find(row=>row.id===expense.supplierCreditForExpenseId):null,party=original||expense;if(party.supplierId?party.supplierId!==supplier.id:party.supplierName!==supplier.name)return sum;return sum+(expense.source==='supplier_credit_note'||expense.supplierCreditForExpenseId?-Math.abs(num(expense.amountNet)):expense.manualPosting?0:dashboardGrossExpense(expense));},0);
  const open = purchases
    .filter((expense) => expense.isSupplierInvoice)
    .reduce((sum, expense) => {
      const summary = dashboardSupplierInvoicePaymentSummary(state, expense);
      return sum + summary.remainingAmount - (summary.overpaidAmount || 0);
    }, 0);
  const importedStats = importedPartyStatsFor(state, 'suppliers', supplier.name);
  const importedOpenItems = importedOpenItemsFor(state, 'supplierInvoices', supplier.name);
  const supplierJournal = window.PlummeSupplierJournalLedger.rows(state, supplier);
  return {
    id: supplier.id,
    nr: supplier.supplierNumber || '',
    name: supplier.name || '',
    org: foreign ? '' : (supplier.organizationNumber || ''), country, registrationNumber: supplier.registrationNumber || (foreign ? supplier.organizationNumber : '') || '', vatNumber: supplier.vatNumber || '',
    email: supplier.email || '',
    phone: supplier.phone || '',
    addr: [supplier.address, supplier.postalCode, supplier.postalPlace].filter(Boolean).join(', '), defaultDueDays: Number.isFinite(Number(supplier.defaultDueDays)) ? Number(supplier.defaultDueDays) : 14, defaultCostAccountCode: supplier.defaultCostAccountCode || '', defaultSettlementAccountCode: supplier.defaultSettlementAccountCode || '',
    out: Math.round(supplierJournal ? (supplierJournal.at(-1)?.balance || 0) : open + importedNetOutstandingFor(state, 'suppliers', 'supplierInvoices', supplier.name)),
    buy: Math.round(buy + num(importedStats.totalPurchases)),
    importedOpenItems,
    status: supplier.status === 'archived' ? 'passiv' : 'aktiv',
  };
};

const mapUiSupplierToBackend = (supplier, existing = {}) => {
  const addressParts = String(supplier.addr || existing.address || '').split(',').map((part) => part.trim()), country = window.PlummeSupplierRecords.supplierCountry(supplier, existing.country || currentCountry()), foreign = window.PlummeSupplierRecords.isForeign({ country }, currentCountry());
  return {
    ...existing,
    id: supplier.id || existing.id || `s${Date.now()}`,
    supplierNumber: cleanPartyNumber(supplier.nr || supplier.supplierNumber || existing.supplierNumber),
    name: supplier.name || existing.name || 'Uten navn',
    organizationNumber: foreign ? '' : supplier.org === '-' ? '' : (supplier.org || existing.organizationNumber || ''), registrationNumber: supplier.registrationNumber === undefined ? (existing.registrationNumber || '') : String(supplier.registrationNumber || '').trim(), vatNumber: supplier.vatNumber === undefined ? (existing.vatNumber || '') : String(supplier.vatNumber || '').trim(),
    email: supplier.email === '-' ? '' : (supplier.email || existing.email || ''),
    phone: supplier.phone === '-' ? '' : (supplier.phone || existing.phone || ''),
    address: addressParts[0] || existing.address || '',
    postalCode: supplier.zip || supplier.postalCode || existing.postalCode || '',
    postalPlace: supplier.city || supplier.postalPlace || addressParts.slice(1).join(', ') || existing.postalPlace || '', defaultDueDays: supplier.defaultDueDays === '' || supplier.defaultDueDays === null || supplier.defaultDueDays === undefined ? (existing.defaultDueDays ?? 14) : Number(supplier.defaultDueDays), defaultCostAccountCode: supplier.defaultCostAccountCode === undefined ? (existing.defaultCostAccountCode || '') : String(supplier.defaultCostAccountCode || ''), defaultSettlementAccountCode: supplier.defaultSettlementAccountCode === undefined ? (existing.defaultSettlementAccountCode || '') : String(supplier.defaultSettlementAccountCode || ''),
    country,
    createdAt: existing.createdAt || nowIso(),
    status: supplier.status === 'passiv' ? 'archived' : 'active',
    archivedAt: supplier.status === 'passiv' ? (existing.archivedAt || nowIso()) : undefined,
  };
};

const mapProductToUi = (product) => ({
  id: product.id,
  name: product.name || '',
  desc: product.description || '',
  nr: product.itemNumber || '',
  price: num(product.unitPrice),
  vat: num(product.vatRate, 25),
  vatSelection: encodeSalesVatSelection(product.vatTreatment, product.vatRate),
  vatTreatment: product.vatTreatment || (num(product.vatRate) === 0 ? 'zeroRated' : 'standard'),
  salesVatCode: product.salesVatCode || '',
  salesAccountCode: product.salesAccountCode || '3000',
  salesAccountEquivalentCode: product.salesAccountEquivalentCode || product.yearClosingStandardAccountCode || '',
  type: product.kind === 'physical' ? 'Vare' : product.kind === 'subscription' ? 'Abonnement' : product.kind === 'rental' ? 'Utleie' : 'Tjeneste',
  period: product.kind === 'subscription' ? String(product.subscriptionPeriodMonths || 1) : undefined,
  rentalPricing: product.kind === 'rental' ? normalizeRentalPricing(product.rentalPricing) : undefined,
  color: product.color || '',
  stock: product.kind === 'physical' || product.kind === 'rental' ? (product.stockQuantity ?? 0) : '-',
  costPrice: product.averageInventoryUnitCost ?? product.inventoryOpeningUnitCost ?? product.costPrice ?? '',
  inventoryTracking: product.inventoryTracking === true,
  inventoryCostMethod: product.inventoryCostMethod || '',
  inventoryTrackingFrom: product.inventoryTrackingFrom || '',
  inventoryOpeningQuantity: product.inventoryOpeningQuantity,
  inventoryOpeningUnitCost: product.inventoryOpeningUnitCost,
  inventoryValue: product.inventoryValue ?? 0, inventoryAccountCode: product.inventoryAccountCode || '1460',
  sku: product.sku || '',
  ean: product.ean || product.gtin || '',
  gtin: product.gtin || product.ean || '',
  productGroup: product.productGroup || '',
  category: product.category || '',
  brand: product.brand || '',
  variantName: product.variantName || '',
  supplierItemNumber: product.supplierItemNumber || '',
  internalReference: product.internalReference || '',
  tags: safeArray(product.tags).map((tag) => String(tag || '').trim()).filter(Boolean),
  status: product.status === 'archived' ? 'passiv' : 'aktiv',
  archivedAt: product.archivedAt || '',
});

const backendDefaultProductSalesAccount = (vat, type) => {
  const treatment = vat?.treatment || '';
  const rate = num(vat?.rate);
  if (treatment === 'exempt') return '3200';
  if (['salesReverseCharge51', 'export52', 'zeroRated'].includes(treatment)) return '3100';
  if (treatment === 'standard') {
    if (rate === 15) return '3030';
    if (rate === 12) return '3050';
    return type === 'Tjeneste' ? '3020' : '3000';
  }
  return type === 'Tjeneste' ? '3020' : '3000';
};

const mapUiProductToBackend = (product, existing = {}) => {
  const vat = normalizeSalesVatOption(product.vatSelection ?? product.vat);
  const specialSalesVatCode = vat.treatment === 'salesReverseCharge51' ? '51' : vat.treatment === 'export52' ? '52' : undefined;
  const type = product.type || (existing.kind === 'physical' ? 'Vare' : existing.kind === 'rental' ? 'Utleie' : existing.kind === 'subscription' ? 'Abonnement' : 'Tjeneste');
  const productField = (key) => Object.prototype.hasOwnProperty.call(product, key) ? product[key] : existing[key];
  const isPhysical = type === 'Vare';
  const inventoryTracking = isPhysical && product.inventoryTracking === true;
  const enablingInventory = inventoryTracking && existing.inventoryTracking !== true;
  const trackingFrom = product.inventoryTrackingFrom || existing.inventoryTrackingFrom || todayIso();
  const openingQuantity = enablingInventory ? Math.max(0, num(product.stock)) : existing.inventoryOpeningQuantity;
  const openingUnitCost = enablingInventory ? Math.max(0, num(product.costPrice)) : existing.inventoryOpeningUnitCost;
  return {
    ...existing,
    id: product.id || existing.id || `p${Date.now()}`,
    name: product.name || existing.name || 'Uten navn',
    description: product.desc || existing.description || '',
    kind: product.type === 'Vare' ? 'physical' : product.type === 'Abonnement' ? 'subscription' : product.type === 'Utleie' ? 'rental' : 'service',
    unitPrice: num(product.price),
    vatRate: vat.rate,
    vatTreatment: vat.treatment,
    salesVatCode: specialSalesVatCode,
    createdAt: existing.createdAt || nowIso(),
    salesAccountCode: product.salesAccountCode || existing.salesAccountCode || backendDefaultProductSalesAccount(vat, type),
    salesAccountEquivalentCode: product.salesAccountEquivalentCode || product.yearClosingStandardAccountCode || existing.salesAccountEquivalentCode || existing.yearClosingStandardAccountCode || undefined,
    subscriptionPeriodMonths: product.type === 'Abonnement' ? Math.max(1, Number(product.period) || 1) : undefined,
    rentalPricing: product.type === 'Utleie' ? normalizeRentalPricing(product.rentalPricing) : undefined,
    color: product.type === 'Abonnement' ? (product.color || existing.color || '#2A6FDB') : undefined,
    stockQuantity: product.type === 'Utleie' || (isPhysical && !inventoryTracking) ? num(product.stock) : (existing.stockQuantity ?? openingQuantity),
    inventoryTracking,
    inventoryCostMethod: inventoryTracking ? 'weighted_average' : undefined,
    inventoryTrackingFrom: inventoryTracking ? trackingFrom : undefined,
    inventoryOpeningQuantity: inventoryTracking ? openingQuantity : undefined,
    inventoryOpeningUnitCost: inventoryTracking ? openingUnitCost : undefined,
    inventoryCostConfirmed: inventoryTracking ? true : undefined, costPrice: product.costPrice === undefined || product.costPrice === '' ? undefined : Math.max(0, num(product.costPrice)),
    inventoryAccountCode: inventoryTracking ? (existing.inventoryAccountCode || '1460') : undefined,
    inventoryChangeAccountCode: inventoryTracking ? (existing.inventoryChangeAccountCode || '4390') : undefined,
    itemNumber: product.nr === '-' ? '' : (product.nr || existing.itemNumber || ''),
    sku: productField('sku') || '',
    ean: productField('ean') || productField('gtin') || '',
    gtin: productField('gtin') || productField('ean') || '',
    productGroup: productField('productGroup') || '',
    category: productField('category') || '',
    brand: productField('brand') || '',
    variantName: productField('variantName') || '',
    supplierItemNumber: productField('supplierItemNumber') || '',
    internalReference: productField('internalReference') || '',
    tags: safeArray(productField('tags')).map((tag) => String(tag || '').trim()).filter(Boolean),
    status: product.status === 'passiv' ? 'archived' : 'active',
    archivedAt: product.status === 'passiv' ? (product.archivedAt || existing.archivedAt || nowIso()) : undefined,
  };
};

const mapInvoicePaymentToUi = (payment) => ({
  id: payment.id,
  amount: num(payment.amount),
  direction: payment.direction || '',
  feeAmount: num(payment.feeAmount),
  feeAccountCode: payment.feeAccountCode || '7770',
  bankAmount: num(payment.bankAmount, num(payment.amount)), currency: payment.currency || '', accountingCurrency: payment.accountingCurrency || '', originalAmount: num(payment.originalAmount), settlementExchangeRate: num(payment.settlementExchangeRate), exchangeDifferenceAmount: num(payment.exchangeDifferenceAmount), exchangeDifferenceAccountCode: payment.exchangeDifferenceAccountCode || '', exchangeDifferenceType: payment.exchangeDifferenceType || '',
  date: isoToNb(payment.date),
  iso: payment.date,
  account: payment.settlementKind === 'no_payment' ? 'Uten betaling' : payment.settlementAccountCode || '1920',
  settlementKind: payment.settlementKind || 'payment',
  createdByFlow: payment.createdByFlow || '',
  note: payment.note || '',
  _backend: payment,
});

const backendRoundMoney = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;

// Same-customer lookups are indexed per invoices array (speed track S0); the two key
// spellings mirror the historical filters exactly so the lists do not change.
const creditNotesByCustomerCache = new WeakMap();
const overpaymentCandidatesByCustomerCache = new WeakMap();
const overpaymentCustomerKey = (entry) => String(entry?.customer?.id || entry?.customerId || entry?.customer?.name || '').trim().toLowerCase();
const invoiceCustomerKey = (invoice) => String(
  invoice?.customer?.id || invoice?.customerId || invoice?.customer?.organizationNumber || invoice?.customer?.name || '',
).trim().toLocaleLowerCase('nb-NO');
const availableCustomerCreditNotes = (state, invoice) => {
  if (!invoice || invoice.kind === 'credit') return [];
  const customerKey = invoiceCustomerKey(invoice);
  if (!customerKey) return [];
  return (indexedBy(creditNotesByCustomerCache, state.invoices, (entry) => entry.kind === 'credit' ? (invoiceCustomerKey(entry) || null) : null)?.get(customerKey) || EMPTY_ROWS)
    .map((entry) => ({ entry, summary: invoicePaymentSummary(state, entry) }))
    .filter(({ summary }) => summary.remainingAmount > 0.005)
    .sort((left, right) => creditNoteOrderKey(left.entry).localeCompare(creditNoteOrderKey(right.entry)))
    .map(({ entry, summary }) => ({
      id: entry.id,
      num: entry.invoiceNumber || entry.id,
      remainingAmount: backendRoundMoney(summary.remainingAmount),
      creditedFromInvoiceId: entry.creditedFromInvoiceId || '',
    }));
};

const mapInvoiceToUi = (invoice, state = runtime.appState) => {
  const net = invoiceNet(invoice);
  const gross = invoiceGross(invoice);
  const paymentSummary = invoicePaymentSummary(state, invoice);
  const displayFactor = invoice.kind === 'credit' ? -1 : 1;
  const relatedCreditNotes = invoice.kind === 'credit' ? [] : creditNotesForInvoice(state, invoice.id)
    .map((entry) => {
      const summary = invoicePaymentSummary(state, entry);
      return {
        id: entry.id,
        num: entry.invoiceNumber || '',
        // Historikken paa fakturaen skal kunne si naar og med hvor mye den ble
        // kreditert, ikke bare at det finnes en kreditnota et sted.
        date: (entry.date || entry.createdAt || '').slice(0, 10),
        amount: backendRoundMoney(Math.abs(invoiceGross(entry))),
        draft: entry.status === 'draft',
        remainingAmount: backendRoundMoney(summary.remainingAmount),
        refundedAmount: backendRoundMoney(summary.paidAmount),
        allocatedAmount: backendRoundMoney(summary.allocatedAmount || 0),
        payments: summary.payments.map(mapInvoicePaymentToUi),
      };
    });
  return {
    id: invoice.id,
    num: invoice.invoiceNumber || invoice.number || invoice.documentNumber || (invoice.status === 'draft' ? 'UTKAST' : invoice.id || ''),
    type: invoiceKindToUi(invoice.kind),
    status: invoiceStatusToUi(invoice, state),
    customer: invoice.customer?.name || 'Uten kunde',
    date: isoToNb(invoice.date || invoice.createdAt),
    due: isoToNb(invoice.dueDate || invoice.validUntil),
    amount: Math.round(Math.abs(gross) * displayFactor),
    net: Math.round(Math.abs(net) * displayFactor),
    draft: invoice.status === 'draft',
    paidAmount: backendRoundMoney(paymentSummary.paidAmount),
    remainingAmount: backendRoundMoney(paymentSummary.remainingAmount),
    overpaidAmount: backendRoundMoney(paymentSummary.overpaidAmount || 0),
    paymentStatus: paymentSummary.status,
    settlementMode: paymentSummary.settlementMode,
    offsetAmount: backendRoundMoney(paymentSummary.offsetAmount),
    refundableAmount: backendRoundMoney(invoice.kind === 'credit' ? paymentSummary.refundableAmount : paymentSummary.overpaidAmount + relatedCreditNotes.reduce((sum, credit) => sum + credit.remainingAmount, 0)),
    creditedAmount: backendRoundMoney(paymentSummary.creditedAmount),
    allocatedCreditAmount: backendRoundMoney(paymentSummary.allocatedCreditAmount || 0),
    allocatedAmount: backendRoundMoney(paymentSummary.allocatedAmount || 0),
    fullyCredited: paymentSummary.fullyCredited,
    creditedFromInvoiceId: invoice.creditedFromInvoiceId || '',
    creditedFromInvoiceNumber: invoice.kind === 'credit'
      ? (invoiceById(state, invoice.creditedFromInvoiceId)?.invoiceNumber || '')
      : '',
    payments: paymentSummary.payments.map(mapInvoicePaymentToUi),
    customerCreditAllocations: customerCreditAllocationsForInvoice(state, invoice.id),
    customerOverpaymentAllocations: customerOverpaymentAllocationsForInvoice(state, invoice.id),
    availableCustomerOverpayments: (overpaymentCustomerKey(invoice)
      ? indexedBy(overpaymentCandidatesByCustomerCache, state.invoices, (entry) => entry.kind !== 'credit' && entry.status !== 'draft' ? (overpaymentCustomerKey(entry) || null) : null)?.get(overpaymentCustomerKey(invoice)) || EMPTY_ROWS
      : EMPTY_ROWS)
      .filter((entry) => entry.id !== invoice.id && invoicePaymentSummary(state, entry).overpaidAmount > 0.005)
      .map((entry) => ({
        id: entry.id,
        num: entry.invoiceNumber || entry.id,
        amount: backendRoundMoney(invoicePaymentSummary(state, entry).overpaidAmount),
      })),
    relatedCreditNotes,
    // Linjer med rest aa kreditere. Tom for kreditnotaer — de krediteres ikke selv.
    creditableLines: invoice.kind === 'credit' ? [] : creditableInvoiceLines(state, invoice),
    availableCreditNotes: availableCustomerCreditNotes(state, invoice),
    createdAt: invoice.createdAt || '',
    sentAt: invoice.sentAt || '',
    deliveredExternallyAt: invoice.deliveredExternallyAt || '',
    deliverySource: invoice.deliverySource || '',
    paidAt: invoice.paidAt || '',
    offerAcceptedAt: invoice.offerAcceptedAt || '',
    // 'manual' = registrert i appen, 'link' = kunden trykte i tilbudslenken.
    offerAcceptedSource: window.PlummeOfferConversion.acceptanceSource(invoice),
    offerAcceptedByEmail: invoice.offerAcceptedByEmail || '',
    convertedFromInvoiceId: invoice.convertedFromInvoiceId || '',
    convertedFromInvoiceNumber: invoice.convertedFromInvoiceId
      ? (invoiceById(state, invoice.convertedFromInvoiceId)?.invoiceNumber || '')
      : '',
    // Bare tilbud trenger listen; den er utledet, ikke lagret, slik at et slettet
    // utkast ikke etterlater et tilbud som pastar at det er fakturert.
    convertedDocuments: invoice.kind === 'offer' ? documentsFromOffer(state, invoice.id) : [],
    orderConfirmationSentAt: invoice.orderConfirmationSentAt || '',
    publicViewedAt: invoice.publicViewedAt || '',
    // Hva som skjedde med den siste e-posten: levert, avvist eller ubesvart.
    delivery: window.PlummeInvoiceDelivery.summary(invoice),
    archivedAt: invoice.archivedAt || '',
    bankAccount: invoice.bankAccount || '',
    bankAccountCode: invoice.bankAccountCode || '', currency: invoice.currency || ({ NO: 'NOK', SE: 'SEK', DK: 'DKK' }[currentCountry()] || 'NOK'), accountingCurrency: invoice.accountingCurrency || ({ NO: 'NOK', SE: 'SEK', DK: 'DKK' }[currentCountry()] || ''), exchangeRate: num(invoice.exchangeRate, 1), exchangeRateDate: invoice.exchangeRateDate || '', exchangeRateSource: invoice.exchangeRateSource || '', originalTotalGross: num(invoice.originalTotalGross, Math.abs(gross)), totalGrossNok: num(invoice.totalGrossNok, Math.abs(gross)),
    _backend: invoice,
  };
};

const mapUiInvoiceToBackend = (invoice, existing = {}) => {
  const date = nbToIso(invoice.date);
  const due = invoice.due && invoice.due !== '-' ? nbToIso(invoice.due) : addDays(date, 14);
  const net = Math.abs(num(invoice.net));
  const gross = Math.abs(num(invoice.amount, net));
  const rate = net ? Math.round((gross / net - 1) * 100) : 25;
  const sign = invoice.type === 'kreditnota' || num(invoice.amount) < 0 ? -1 : 1;
  return {
    ...existing,
    id: invoice.id || existing.id || `inv${Date.now()}`,
    invoiceNumber: invoice.num && invoice.num !== 'UTKAST' ? invoice.num : '',
    customer: existing.customer || {
      name: invoice.customer || 'Uten kunde',
      organizationNumber: '',
      email: '',
      phone: '',
      address: '',
      postalCode: '',
      postalPlace: '',
    },
    date,
    dueDate: due,
    validUntil: invoice.type === 'tilbud' ? due : existing.validUntil,
    lines: existing.lines && existing.lines.length ? existing.lines : [{
      id: `line${Date.now()}`,
      description: invoice.descr || 'Overfort fra Plumme 3',
      quantity: 1,
      unitPrice: sign * net,
      vatRate: [25, 15, 12, 10, 6, 0].includes(rate) ? rate : 25,
      vatTreatment: 'standard',
      salesAccountCode: '3000',
    }],
    createdAt: existing.createdAt || nowIso(),
    kind: uiTypeToInvoiceKind(invoice.type),
    status: uiStatusToInvoiceStatus(invoice.status),
    paidAt: invoice.status === 'betalt' ? (existing.paidAt || nowIso()) : existing.paidAt,
    sentAt: invoice.status === 'sendt' || invoice.status === 'forfalt' ? (existing.sentAt || nowIso()) : existing.sentAt,
  };
};

const receiptInboxEntryIsTrashed = (entry) => ['trashed', 'trash'].includes(String(entry?.status || ''));
const receiptInboxEntryIsBankLinked = (entry) => String(entry?.status || '') === 'linked-bank-line'; const receiptInboxEntryIsSupportingDocument = (entry) => String(entry?.status || '') === 'linked-supporting-document';

const mapReceiptToUi = (entry) => ({
  id: entry.id,
  name: entry.attachmentName || 'Bilag',
  subject: entry.receiptAnalysis?.supplierName || entry.attachmentName || 'Uten emnefelt',
  received: (entry.createdAt || '').slice(0, 10),
  sum: entry.receiptAnalysis?.totalGross ?? null,
  status: receiptInboxEntryIsSupportingDocument(entry) ? 'linked-supporting-document' : receiptInboxEntryIsBankLinked(entry) ? 'linked-bank-line' : entry.status === 'booked' ? 'booked' : receiptInboxEntryIsTrashed(entry) ? 'trashed' : entry.receiptAnalysis?.status === 'completed' ? 'klar' : 'mangler',
  file: entry.file || undefined,
  attachmentId: entry.attachmentId,
  attachmentName: entry.attachmentName,
  attachmentMimeType: entry.attachmentMimeType,
  createdAt: entry.createdAt,
  source: entry.source,
  projectId: entry.projectId,
  projectBillable: entry.projectBillable,
  bookedAt: entry.bookedAt,
  bookedAs: entry.bookedAs,
  linkedEntryId: entry.linkedEntryId,
  trashedAt: entry.trashedAt,
  purgeAfter: entry.purgeAfter,
  receiptAnalysis: entry.receiptAnalysis,
  receiptWorkflowStatus: entry.receiptWorkflowStatus,
  workflowSteps: entry.workflowSteps,
  confidenceScore: entry.confidenceScore,
  riskFlags: entry.riskFlags,
  approvalRoute: entry.approvalRoute,
  workflowAuditTrail: entry.workflowAuditTrail,
  attachments: safeArray(entry.attachments).map((attachment) => ({
    id: attachment.id || attachment.storagePath || attachment.name,
    name: attachment.name || entry.attachmentName || 'Bilag',
    file: attachment.downloadUrl || attachment.file || entry.file,
    downloadUrl: attachment.downloadUrl || attachment.file || '',
    storagePath: attachment.storagePath || '',
    mimeType: attachment.mimeType || '',
  })).filter((attachment) => attachment.file || attachment.storagePath),
  _backend: entry,
});

const mapUiReceiptToBackend = (entry, existing = {}) => ({
  ...existing,
  id: entry.id || existing.id || `receipt${Date.now()}`,
  attachmentId: existing.attachmentId || entry.attachmentId || entry.id || `attachment${Date.now()}`,
  attachmentName: entry.name || existing.attachmentName || 'Bilag',
  attachmentMimeType: existing.attachmentMimeType || 'application/pdf',
  createdAt: entry.received ? `${entry.received}T00:00:00.000Z` : (existing.createdAt || nowIso()),
  source: existing.source || 'upload',
  status: entry.status === 'klar' ? 'booked' : 'pending',
  file: entry.file || existing.file,
  attachments: safeArray(entry.attachments).length
    ? safeArray(entry.attachments).map((attachment) => stripUndefinedDeep({
        id: attachment.id || attachment.storagePath || `att${Date.now()}`,
        name: attachment.name || entry.name || 'Bilag',
        downloadUrl: attachment.downloadUrl || attachment.file || '',
        storagePath: attachment.storagePath || '',
        mimeType: attachment.mimeType || '',
      }))
    : existing.attachments,
  receiptAnalysis: {
    ...(existing.receiptAnalysis || {}),
    status: entry.status === 'klar' ? 'completed' : 'pending',
    supplierName: entry.subject || existing.receiptAnalysis?.supplierName,
    totalGross: entry.sum ?? existing.receiptAnalysis?.totalGross,
  },
});

const mapMileageToUi = (entry) => ({
  ...entry, id: entry.id,
  date: entry.date || todayIso(),
  fromLocation: entry.fromLocation || '',
  toLocation: entry.toLocation || '',
  stops: safeArray(entry.stops).map((stop) => String(stop || '').trim()).filter(Boolean),
  purpose: entry.purpose || '',
  usageType: entry.usageType === 'private' || entry.privateUse === true ? 'private' : 'business',
  privateUse: entry.usageType === 'private' || entry.privateUse === true, returnTrip: entry.returnTrip === true,
  kilometers: num(entry.kilometers),
  ratePerKm: num(entry.ratePerKm, 3.5),
  amountNet: entry.usageType === 'private' || entry.privateUse === true ? 0 : num(entry.amountNet),
  status: entry.status === 'annulled' ? 'annullert' : (entry.recordedSeq ? 'bokfort' : 'registered'),
});

const mapUiMileageToBackend = (entry, existing = {}) => {
  const usageType = entry.usageType === 'private' || entry.privateUse === true ? 'private' : 'business';
  return {
    ...existing,
    id: entry.id || existing.id || `m${Date.now()}`,
    date: entry.date || existing.date || todayIso(),
    fromLocation: entry.fromLocation || existing.fromLocation || '',
    toLocation: entry.toLocation || existing.toLocation || '',
    stops: safeArray(entry.stops).map((stop) => String(stop || '').trim()).filter(Boolean),
    purpose: entry.purpose || existing.purpose || '',
    usageType,
    privateUse: usageType === 'private', returnTrip: entry.returnTrip === true, // a return trip stores the driven total in kilometers
    kilometers: num(entry.kilometers),
    ratePerKm: num(entry.ratePerKm, 3.5),
    amountNet: usageType === 'private' ? 0 : num(entry.amountNet, num(entry.kilometers) * num(entry.ratePerKm, 3.5)),
    createdAt: existing.createdAt || nowIso(),
    status: entry.status === 'annullert' ? 'annulled' : 'active',
  };
};

const mapEmployeeToUi = (employee) => ({
  id: employee.id,
  name: employee.name || '',
  initials: (employee.name || '').split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase(),
  lonnstype: employee.payType === 'monthly' ? 'Fastlønn' : 'Timelønn',
  active: employee.active !== false,
  fixedPayrollLines: safeArray(employee.fixedPayrollLines).map((line) => ({ ...line })),
  documents: safeArray(employee.documents).map((document) => ({
    id: document.id,
    name: document.name || 'Dokument',
    mimeType: document.mimeType || '',
    size: num(document.size),
    description: document.description || '',
    uploadedAt: document.uploadedAt || '',
    storagePath: document.storagePath || '',
    downloadUrl: document.downloadUrl || '',
  })),
  _backend: employee,
  personalia: {
    arbeidsgiver: employee.isMainEmployer === false ? 'Biarbeidsgiver' : 'Hovedarbeidsgiver',
    adresse: [employee.addressLine1, employee.postalCode, employee.postalPlace].filter(Boolean).join(', ') || '-',
    fnr: employee.nationalIdentityNumber || '-',
    epost: employee.email || '-',
    telefon: employee.phone || '-',
    bankkonto: employee.bankAccount || '-',
    feriepenger: `${employee.holidayPayRate ?? 10.2} %`,
    skattekort: employee.taxCardStatus === 'fetched' ? 'Hentet' : 'Ikke hentet fra Altinn',
    notat: employee.note || '-',
  },
  arbeidsforhold: {
    periode: `${isoToNb(employee.employmentStartDate)} - ${employee.employmentEndDate ? isoToNb(employee.employmentEndDate) : 'løpende'}`,
    type: employee.employmentType || 'ordinary',
    form: employee.employmentForm || 'permanent',
    tidsordning: employee.workSchedule || 'none',
    timer: String(employee.weeklyHours ?? 37.5),
    stillingsprosent: `${employee.positionPercent ?? 100} %`,
    permisjoner: 'Ingen registrert',
  },
});

const parsePercent = (value, fallback = 0) => {
  const match = String(value ?? '').replace(',', '.').match(/-?\d+(?:\.\d+)?/);
  if (!match) return fallback;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseNbDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const match = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : raw.slice(0, 10);
};

const fixedPayrollLineDefaults = (payType = 'monthly-salary') => {
  const definition = getPayrollPayTypeDefinition(payType);
  if (definition) {
    return {
      payout: definition.kind !== 'Trekk' && definition.kind !== 'Naturalytelse' && payType !== 'pension-premium',
      taxable: definition.tax,
      employerContribution: definition.aga,
      holidayPay: definition.holiday,
      pension: definition.pension,
    };
  }
  if (payType === 'car-km-allowance') {
    return { payout: true, taxable: false, employerContribution: false, holidayPay: false, pension: false };
  }
  if (payType === 'holiday-pay-current' || payType === 'holiday-pay-previous' || payType === 'pension-premium') {
    return { payout: payType !== 'pension-premium', taxable: false, employerContribution: payType !== 'pension-premium', holidayPay: false, pension: false };
  }
  return { payout: true, taxable: true, employerContribution: true, holidayPay: true, pension: true };
};

const normalizeFixedPayrollLine = (line = {}, employee = {}, existingLine = {}) => {
  const payType = line.payType || existingLine.payType || 'monthly-salary';
  const defaults = fixedPayrollLineDefaults(payType);
  const amount = Number(line.amount ?? existingLine.amount);
  return stripUndefinedDeep({
    id: line.id || existingLine.id || createId('fixed-pay'),
    employmentLabel: line.employmentLabel || existingLine.employmentLabel || employee.name || 'Ansatt',
    payType,
    amount: Number.isFinite(amount) ? Math.max(0, amount) : undefined,
    quantity: line.quantity ?? existingLine.quantity ?? undefined,
    rate: line.rate ?? existingLine.rate ?? undefined,
    repeatInterval: line.repeatInterval || existingLine.repeatInterval || 'payroll-run',
    variableAmount: line.variableAmount === true || existingLine.variableAmount === true,
    payout: line.payout ?? existingLine.payout ?? defaults.payout,
    taxable: line.taxable ?? existingLine.taxable ?? defaults.taxable,
    employerContribution: line.employerContribution ?? existingLine.employerContribution ?? defaults.employerContribution,
    holidayPay: line.holidayPay ?? existingLine.holidayPay ?? defaults.holidayPay,
    pension: line.pension ?? existingLine.pension ?? defaults.pension,
    deduction: line.deduction ?? existingLine.deduction ?? false,
    calculationRuleOverride: line.calculationRuleOverride === true || undefined,
    calculationRuleOverrideReason: line.calculationRuleOverride === true ? line.calculationRuleOverrideReason : undefined,
    editTaxResidence: line.editTaxResidence === true || existingLine.editTaxResidence === true,
    taxResidenceCountry: line.taxResidenceCountry || existingLine.taxResidenceCountry || undefined,
    editEarningCountry: line.editEarningCountry === true || existingLine.editEarningCountry === true,
    earningCountry: line.earningCountry || existingLine.earningCountry || undefined,
    editIncomeRelation: line.editIncomeRelation === true || existingLine.editIncomeRelation === true,
    incomeRelation: line.incomeRelation || existingLine.incomeRelation || undefined,
    comment: line.comment || existingLine.comment || undefined,
    createdAt: existingLine.createdAt || line.createdAt || nowIso(),
    updatedAt: nowIso(),
  });
};

const mapUiEmployeeToBackend = (employee, existing = {}) => {
  const personalia = employee.personalia || {};
  const arbeidsforhold = employee.arbeidsforhold || {};
  const addressParts = String(personalia.adresse || '').split(',').map((part) => part.trim()).filter(Boolean);
  const [parsedAddressLine1 = '', parsedPostalCode = '', parsedPostalPlace = ''] = addressParts;
  const addressLine1 = employee._backend?.addressLine1 ?? parsedAddressLine1 ?? existing.addressLine1 ?? '';
  const postalCode = employee._backend?.postalCode ?? parsedPostalCode ?? existing.postalCode ?? '';
  const postalPlace = employee._backend?.postalPlace ?? parsedPostalPlace ?? existing.postalPlace ?? '';
  const periodParts = String(arbeidsforhold.periode || '').split(/\s*[–-]\s*/);
  const employmentStartDate = parseNbDate(employee.employmentStartDate || periodParts[0]) || existing.employmentStartDate || todayIso();
  const periodEnd = periodParts[1] && !/lopende|løpende/i.test(periodParts[1]) ? parseNbDate(periodParts[1]) : '';
  const backendHasEndDate = Object.prototype.hasOwnProperty.call(employee._backend || {}, 'employmentEndDate');
  return stripUndefinedDeep({
    ...existing,
    ...employee._backend,
    id: employee.id || existing.id || createId('emp'),
    name: String(employee.name || existing.name || '').trim(),
    email: personalia.epost === '-' ? '' : personalia.epost || existing.email || '',
    nationalIdentityNumber: personalia.fnr === '-' ? '' : personalia.fnr || existing.nationalIdentityNumber || '',
    phone: personalia.telefon === '-' ? '' : personalia.telefon || existing.phone || '',
    addressLine1,
    postalCode,
    postalPlace,
    bankAccount: personalia.bankkonto === '-' ? '' : personalia.bankkonto || existing.bankAccount || '',
    employmentStartDate,
    employmentEndDate: employee.active === false
      ? (periodEnd || employee._backend?.employmentEndDate || existing.employmentEndDate || todayIso())
      : (backendHasEndDate ? employee._backend?.employmentEndDate : (periodEnd || existing.employmentEndDate)),
    employmentType: arbeidsforhold.type || existing.employmentType || 'ordinary',
    employmentForm: arbeidsforhold.form || existing.employmentForm || 'permanent',
    workSchedule: arbeidsforhold.tidsordning || existing.workSchedule || 'none',
    weeklyHours: parsePercent(arbeidsforhold.timer, existing.weeklyHours ?? 37.5),
    positionPercent: parsePercent(arbeidsforhold.stillingsprosent, existing.positionPercent ?? 100),
    payType: String(employee.lonnstype || '').toLowerCase().includes('fast') ? 'monthly' : existing.payType || 'hourly',
    taxDeductionPercent: num(employee.taxDeductionPercent ?? existing.taxDeductionPercent, 30),
    employerContributionRate: num(employee.employerContributionRate ?? existing.employerContributionRate, 14.1),
    holidayPayRate: parsePercent(personalia.feriepenger, existing.holidayPayRate ?? 10.2),
    isMainEmployer: personalia.arbeidsgiver !== 'Biarbeidsgiver',
    taxCardStatus: String(personalia.skattekort || '').toLowerCase().startsWith('hentet') ? 'fetched' : existing.taxCardStatus || 'not-fetched',
    note: personalia.notat === '-' ? '' : personalia.notat || existing.note || '',
    fixedPayrollLines: safeArray(employee.fixedPayrollLines || employee._backend?.fixedPayrollLines || existing.fixedPayrollLines)
      .map((line) => normalizeFixedPayrollLine(line, { ...existing, ...employee })),
    documents: safeArray(employee.documents || employee._backend?.documents || existing.documents).map((document) => stripUndefinedDeep({
      id: document.id || createId('emp-doc'),
      name: document.name || 'Dokument',
      mimeType: document.mimeType || '',
      size: Number.isFinite(Number(document.size)) ? Math.max(0, Number(document.size)) : undefined,
      description: document.description || undefined,
      uploadedAt: document.uploadedAt || nowIso(),
      storagePath: document.storagePath || undefined,
      downloadUrl: document.downloadUrl || undefined,
    })),
    active: employee.active !== false,
    createdAt: existing.createdAt || nowIso(),
  });
};

const buildCompany = (state) => ({
  name: state.settings.businessName || 'Mitt foretak',
  orgnr: state.settings.organizationNumber || '',
  konto: state.settings.bankAccount || state.settings.bankAccounts?.find((a) => a.invoiceDefault)?.accountNumber || '',
  addr: [
    state.settings.address,
    [state.settings.postalCode, state.settings.postalPlace].filter(Boolean).join(' '),
  ].filter(Boolean).join(', '),
  email: isLegacyDesignBusinessEmail(state.settings.email) ? '' : (state.settings.email || ''),
  logoDownloadUrl: state.settings.logoDownloadUrl || '',
  logoStoragePath: state.settings.logoStoragePath || '',
  logoId: state.settings.logoId || '',
  logoName: state.settings.logoName || '',
});

const voucherMoney = (amount) => Math.sign(num(amount)) * Math.round(Math.abs(num(amount)) * 100 + 1e-8) / 100;
const posting = (nr, name, desc, amount, metadata = {}) => ({ nr, name, desc, amount: voucherMoney(amount), ...metadata });
const norwegianOutputVatAccount = (rate) => Math.abs(num(rate) - 15) < 0.001 ? '2701'
  : Math.abs(num(rate) - 11.11) < 0.001 ? '2702'
    : Math.abs(num(rate) - 12) < 0.001 ? '2703' : '2700';
const norwegianInputVatAccount = (rate) => Math.abs(num(rate) - 15) < 0.001 ? '2711'
  : Math.abs(num(rate) - 11.11) < 0.001 ? '2712'
    : Math.abs(num(rate) - 12) < 0.001 ? '2713' : '2710';

// Kreditnotaer finnes med to fortegnskonvensjoner i data: appens egne har negative
// linjebelop (creditInvoice snur unitPrice), mens importerte og eldre notaer har
// positive. Fortegnet normaliseres her ved lesing i stedet for i lagringen, slik at
// begge foeres likt og ingen gamle bilag maa migreres. Faktoren gjelder hele bilaget,
// ikke linje for linje, saa foeringen balanserer uansett hvordan linjene ser ut.
const invoicePostingFactor = (invoice) => (invoice.kind === 'credit' && invoiceGross(invoice) > 0 ? -1 : 1);
const invoicePostingSalesAccount = (line) => {
  if (line.salesAccountCode) return line.salesAccountCode;
  const treatment = line.vatTreatment || (num(line.vatRate) === 0 ? 'zeroRated' : 'standard');
  return num(line.vatRate) === 0 || treatment === 'zeroRated' || treatment === 'exempt' ? '3100' : '3000';
};

const invoicePostings = (invoice) => {
  const gross = voucherMoney(invoiceGross(invoice));
  const factor = invoicePostingFactor(invoice);
  const salesPostings = safeArray(invoice.lines).filter(invoiceBackendLineHasAmount).map((line) =>
    posting(invoicePostingSalesAccount(line), 'Salgsinntekt', line.description || invoice.customer?.name || 'Salg', -(invoice.accountingRoundingVersion === 'NO-VAT-BUCKET-1' ? invoice.accountingAmounts.lines[invoice.lines.indexOf(line)].net : invoiceLineNet(line)) * factor),
  );
  const vatPostings = safeArray(invoice.lines).filter((line) => invoiceBackendLineHasAmount(line)
    && num(line.vatRate) > 0 && (!line.vatTreatment || line.vatTreatment === 'standard'))
    .map((line) => posting(norwegianOutputVatAccount(line.vatRate), 'Utg\u00e5ende mva', `MVA ${invoice.invoiceNumber || invoice.id}`, -(invoice.accountingRoundingVersion === 'NO-VAT-BUCKET-1' ? invoice.accountingAmounts.lines[invoice.lines.indexOf(line)].vat : voucherMoney(invoiceLineNet(line) * num(line.vatRate) / 100)) * factor));
  const expectedVat = voucherMoney(gross * factor + salesPostings.reduce((sum, line) => sum + line.amount, 0));
  if (vatPostings.length) {
    const currentVat = -vatPostings.reduce((sum, line) => sum + line.amount, 0);
    vatPostings[vatPostings.length - 1].amount = voucherMoney(vatPostings[vatPostings.length - 1].amount - (expectedVat - currentVat));
  } else if (salesPostings.length && expectedVat) {
    salesPostings[salesPostings.length - 1].amount = voucherMoney(salesPostings[salesPostings.length - 1].amount - expectedVat);
  }
  const inventoryPostings = safeArray(invoice.lines).flatMap((line) => {
    const amount = backendRoundMoney(line.inventoryCostAmount);
    if (!(amount > 0) || line.inventoryCostMethod !== 'weighted_average') return [];
    const inventoryFactor = invoice.kind === 'credit' ? -1 : 1;
    return [
      posting(line.inventoryChangeAccountCode || '4390', 'Varekost', line.description || 'Varekost solgte varer', amount * inventoryFactor),
      posting(line.inventoryAccountCode || '1460', 'Varelager', line.description || 'Lageravgang solgte varer', -amount * inventoryFactor),
    ];
  });
  return [
    posting('1500', 'Kundefordringer', invoice.customer?.name || 'Kundefordring', gross * factor),
    ...salesPostings,
    ...vatPostings,
    ...inventoryPostings,
  ].filter(Boolean);
};

const manualVatSummary = (expense) => {
  const result = safeArray(expense.manualPostingLines).reduce((summary, line) => {
    const country = expense.country || expense.manualPostingCountry || 'NO';
    [
      { side: 'from', account: line.fromAccount, amount: line.fromAmount, vatCode: line.fromVatCode },
      { side: 'to', account: line.toAccount, amount: line.toAmount, vatCode: line.toVatCode },
    ].forEach((leg) => {
      const calculationVersion = expense.manualPostingVatCalculationVersion || '';
      const split = manualVatSplit(leg.amount, leg.vatCode, country, calculationVersion, leg.account);
      if (!split.reportCode) return;
      const sign = leg.side === 'from' ? -1 : 1; const key = `${split.reportCode}:${split.rate}:${split.kind}`;
      const row = summary.rows.get(key) || { code: split.reportCode, rate: split.rate, kind: split.kind, base: 0, incoming: 0, outgoing: 0 };
      if (split.kind === 'basis') {
        row.base += split.base * sign;
      } else if (split.kind === 'input' || split.kind === 'inputOnly') {
        summary.incoming += split.vat * sign;
        summary.inputBase += split.base * sign;
        row.incoming += split.vat * sign;
        row.base += split.base * sign;
      } else if (split.kind === 'output') {
        const reportSign = ['NO-MANUAL-VAT-2', 'NO-MANUAL-VAT-3'].includes(calculationVersion) ? -sign : sign;
        summary.outgoing += split.vat * reportSign;
        summary.outputBase += split.base * reportSign;
        row.outgoing += split.vat * reportSign;
        row.base += split.base * reportSign;
      } else if (split.kind === 'reverse') {
        summary.incoming += split.vat * sign;
        summary.outgoing += split.vat * sign;
        summary.inputBase += split.base * sign;
        summary.outputBase += split.base * sign;
        row.incoming += split.vat * sign;
        row.outgoing += split.vat * sign;
        row.base += split.base * sign;
      }
      summary.rows.set(key, row);
    });
    return summary;
  }, { incoming: 0, outgoing: 0, inputBase: 0, outputBase: 0, rows: new Map() });
  return { ...result, rows: [...result.rows.values()] };
};

const manualPostingLegPostings = ({ side, account, label, amount, vatCode, description, country, calculationVersion }) => {
  const sign = side === 'from' ? -1 : 1;
  const split = manualVatSplit(amount, vatCode, country, calculationVersion, account);
  const lines = split.accountAmount ? [posting(account, label, description, sign * split.accountAmount,
    split.kind === 'basis' ? { saftTaxInformation: { code: split.reportCode, rate: split.rate, base: split.base, amount: 0, side: sign > 0 ? 'debit' : 'credit' } } : {})] : [];
  if (split.kind === 'input' || split.kind === 'inputOnly') {
    lines.push(posting(norwegianInputVatAccount(split.rate), 'Inng\u00e5ende mva', description, sign * split.vat,
      split.kind === 'inputOnly' ? { saftTaxInformation: { code: split.reportCode, rate: split.rate, base: 0, amount: split.vat, side: sign > 0 ? 'debit' : 'credit' } } : {}));
  } else if (split.kind === 'output') {
    lines.push(posting(norwegianOutputVatAccount(split.rate), 'Utg\u00e5ende mva', description, sign * split.vat));
  } else if (split.kind === 'reverse' && split.vat) {
    lines.push(posting(norwegianInputVatAccount(split.rate), 'Inng\u00e5ende mva snudd avgift', description, sign * split.vat));
    lines.push(posting(norwegianOutputVatAccount(split.rate), 'Utg\u00e5ende mva snudd avgift', description, -sign * split.vat));
  }
  return lines;
};

const saleVatReportSummary = (state, year, country = currentCountry(), options = {}) => {
  const summary = {
    outgoing: 0,
    standardBase: 0,
    taxableByRate: {},
    zeroRatedBase: 0,
    exemptBase: 0,
    reverseChargeSalesBase: 0,
    exportBase: 0,
  };
  safeArray(state.invoices)
    .filter((invoice) => reportDateInRange(invoice.date, year, options) && isPostedInvoice(invoice, state))
    .forEach((invoice) => {
      const factor = invoicePostingFactor(invoice);
      safeArray(invoice.lines).forEach((line) => {
        const amounts = invoice.accountingRoundingVersion === 'NO-VAT-BUCKET-1' ? invoice.accountingAmounts.lines[invoice.lines.indexOf(line)] : null, base = (amounts ? amounts.net : invoiceLineNet(line)) * factor;
        const treatment = invoiceLineVatTreatment(line);
        if (treatment === 'standard') {
          const rate = num(line.vatRate);
          summary.standardBase += base;
          summary.taxableByRate[String(rate)] = num(summary.taxableByRate[String(rate)]) + base;
          summary.outgoing += amounts ? amounts.vat * factor : base * rate / 100;
        } else if (country === 'NO' && treatment === 'salesReverseCharge51') {
          summary.reverseChargeSalesBase += base;
        } else if (country === 'NO' && treatment === 'export52') {
          summary.exportBase += base;
        } else if (treatment === 'zeroRated') {
          summary.zeroRatedBase += base;
        } else {
          summary.exemptBase += base;
        }
      });
    });
  safeArray(state.otherSales).flatMap((sale) => sale?.status === 'annulled' ? [{ sale, date: sale.date, factor: 1 },
    { sale, date: globalThis.PlummeAccountingScope.accountingEventDate(state, sale.reversalDate || sale.annulledAt || sale.updatedAt || sale.date), factor: -1 }] : isActiveOtherSale(sale) ? [{ sale, date: sale.date, factor: 1 }] : [])
    .filter((event) => reportDateInRange(event.date, year, options) && event.sale.vatTreatment !== 'vatOnly').flatMap((event) => globalThis.PlummeOtherSaleAmounts.groups(event.sale).map((group) => ({ ...group, amountNet: group.amountNet * event.factor })))
    .forEach(({ amountNet: base, vatTreatment: treatment, vatRate: rate }) => {
      if (treatment === 'standard') {
        summary.standardBase += base;
        summary.taxableByRate[String(rate)] = num(summary.taxableByRate[String(rate)]) + base;
        summary.outgoing += base * rate / 100;
      } else if (country === 'NO' && treatment === 'salesReverseCharge51') {
        summary.reverseChargeSalesBase += base;
      } else if (country === 'NO' && treatment === 'export52') {
        summary.exportBase += base;
      } else if (treatment === 'zeroRated') {
        summary.zeroRatedBase += base;
      } else {
        summary.exemptBase += base;
      }
    });
  return summary;
};

const expensePostings = (expense) => {
  if (expense.manualPosting && safeArray(expense.manualPostingLines).length) {
    const country = expense.country || expense.manualPostingCountry || 'NO';
    return safeArray(expense.manualPostingLines).flatMap((line) => [
      ...manualPostingLegPostings({
        side: 'from',
        account: line.fromAccount || accountCodeFromLabel(line.fromAccountLabel, '1920'),
        label: line.fromAccountLabel || 'Fra konto',
        amount: line.fromAmount,
        vatCode: line.fromVatCode,
        description: expense.description,
        country,
        calculationVersion: expense.manualPostingVatCalculationVersion || '',
      }).map(entry=>({...entry,...(line.fromAssetId?{assetId:line.fromAssetId}:{})})),
      ...manualPostingLegPostings({
        side: 'to',
        account: line.toAccount || accountCodeFromLabel(line.toAccountLabel, '7790'),
        label: line.toAccountLabel || 'Til konto',
        amount: line.toAmount,
        vatCode: line.toVatCode,
        description: expense.description,
        country,
        calculationVersion: expense.manualPostingVatCalculationVersion || '',
      }).map(entry=>({...entry,...(line.toAssetId?{assetId:line.toAssetId}:{})})),
    ]);
  }
  const net = num(expense.amountNet);
  const vat = net * num(expense.vatRate) / 100;
  const gross = net + vat;
  const costAccount = expense.accountCode || expense.expenseAccountCode || expense.categoryAccountCode || '6300';
  const creditAccount = expense.isSupplierInvoice && !expense.supplierInvoicePaidAt ? '2400' : '1920';
  return [
    posting(costAccount, expense.category || 'Kostnad', expense.description || expense.supplierName || 'Kj\u00f8p', net),
    vat ? posting('2710', 'Inng\u00e5ende mva', `MVA ${expense.voucherNumber || expense.id}`, vat) : null,
    posting(creditAccount, creditAccount === '2400' ? 'Leverand\u00f8rgjeld' : 'Bankinnskudd', expense.supplierName || expense.description || 'Kj\u00f8p', -gross),
  ].filter(Boolean);
};

// \u00abBetalt til\u00bb for annet salg \u2014 hvor pengene kom inn styrer debetkontoen i bokf\u00f8ringen.
const OTHER_SALE_PAYMENT_SOURCES = {
  company: { code: '1920', name: 'Bankinnskudd' },
  cash: { code: '1900', name: 'Kontanter' },
  private: { code: '2060', name: 'Privatuttak' },
};
const OTHER_SALE_SETTLEMENT_NAMES = { '1920': 'Bankinnskudd', '1900': 'Kontanter', '2060': 'Privatuttak' };
const otherSalePaymentMeta = (source) => OTHER_SALE_PAYMENT_SOURCES[String(source || '').trim()] || OTHER_SALE_PAYMENT_SOURCES.company;
const normalizeOtherSalePaymentSource = (source) => (OTHER_SALE_PAYMENT_SOURCES[String(source || '').trim()] ? String(source).trim() : 'company');
const otherSaleSettlementName = (code, fallback) => OTHER_SALE_SETTLEMENT_NAMES[String(code || '').trim()] || plumme1AccountName(String(code || '').trim(), fallback || 'Oppgjørskonto');

const otherSalePostings = (sale, country = currentCountry()) => {
  if (country !== 'NO') return [];
  const net = voucherMoney(sale.amountNet);
  const settlement = otherSalePaymentMeta(sale.paymentSource);
  const settlementCode = sale.settlementAccountCode || settlement.code;
  const settlementName = otherSaleSettlementName(settlementCode, settlement.name);
  if (sale.vatTreatment === 'vatOnly') {
    return net > 0
      ? [
        posting(settlementCode, settlementName, sale.description || 'MVA-oppgjør', net),
        posting('2700', 'Utgående mva', `MVA ${sale.voucherNumber || sale.id}`, -net),
      ]
      : [];
  }
  // Salgslinjene fores per inntektskonto og MVA-sats, med netto etter rabatt.
  const amounts = globalThis.PlummeOtherSaleAmounts;
  const { fee: feeAmount, payout: expectedPayout } = amounts.totals(sale);
  const payoutAmount = sale.settlementPayoutAmount === undefined || sale.settlementPayoutAmount === null || sale.settlementPayoutAmount === ''
    ? expectedPayout
    : voucherMoney(sale.settlementPayoutAmount);
  const splits = amounts.splits(sale);
  // Nye salg med delbetalinger som ikke stemmer avvises ved lagring. Eldre lagrede
  // avvik fores fortsatt balansert paa oppgjorskontoen.
  const settlementPostings = splits.length && !amounts.splitMismatch(sale)
    ? splits.map((split) => posting(split.settlementAccountCode || settlementCode, otherSaleSettlementName(split.settlementAccountCode || settlementCode, settlement.name), sale.description || 'Annet salg', voucherMoney(split.amount)))
    : [posting(settlementCode, settlementName, sale.description || 'Annet salg', Math.abs(payoutAmount - expectedPayout) <= 0.005 ? payoutAmount : expectedPayout)];
  return [
    ...settlementPostings,
    feeAmount > 0 ? posting(sale.settlementFeeAccountCode || sale.feeAccountCode || '7770', plumme1AccountName(sale.settlementFeeAccountCode || sale.feeAccountCode || '7770', 'Bank- og kortgebyr'), sale.description || 'Annet salg', feeAmount) : null,
    ...amounts.groups(sale).map((group) => posting(group.salesAccountCode, 'Salg og uttak', sale.description || 'Annet salg', -group.amountNet)),
    ...amounts.vatByRate(sale).map((entry) => posting(norwegianOutputVatAccount(entry.rate), 'Utgående mva', `MVA ${sale.voucherNumber || sale.id}`, -entry.vat)),
  ].filter(Boolean);
};

const vatReportCodesForSaleTreatment = (treatment, country = currentCountry(), vatRate = 25) => {
  if (country !== 'NO') {
    if (treatment === 'standard') return ['sales'];
    if (treatment === 'zeroRated') return ['zero'];
    return ['outside'];
  }
  if (treatment === 'standard') return [num(vatRate) === 15 ? '31' : num(vatRate) === 12 ? '33' : '3'];
  if (treatment === 'salesReverseCharge51') return ['51'];
  if (treatment === 'export52') return ['52'];
  if (treatment === 'zeroRated' || treatment === 'exempt') return [treatment === 'exempt' ? '6' : '5'];
  return ['0'];
};

const invoiceVatReportCodes = (invoice, country = currentCountry()) =>
  Array.from(new Set(safeArray(invoice.lines).filter(invoiceBackendLineHasAmount).flatMap((line) =>
    vatReportCodesForSaleTreatment(invoiceLineVatTreatment(line), country, line.vatRate),
  )));

const otherSaleVatReportCodes = (sale, country = currentCountry()) => {
  if (sale.vatTreatment === 'vatOnly') return [];
  return Array.from(new Set(globalThis.PlummeOtherSaleAmounts.groups(sale).flatMap((group) => vatReportCodesForSaleTreatment(group.vatTreatment, country, group.vatRate))));
};

const expenseVatReportCodes = (expense, country = currentCountry()) => {
  if (country !== 'NO') return ['purchases'];
  if (expense.manualPosting && safeArray(expense.manualPostingLines).length) {
    const codes = safeArray(expense.manualPostingLines).flatMap((line) => [
      manualVatSplit(line.fromAmount, line.fromVatCode, country, expense.manualPostingVatCalculationVersion || '', line.fromAccount).reportCode,
      manualVatSplit(line.toAmount, line.toVatCode, country, expense.manualPostingVatCalculationVersion || '', line.toAccount).reportCode,
    ]).filter(Boolean);
    return Array.from(new Set(codes));
  }
  const reverseCodes = safeArray(expense.purchaseLines).length
    ? safeArray(expense.purchaseLines).map((line) => line.reverseChargeCode).filter((code) => ['81', '83', '86', '88'].includes(String(code)))
    : [expense.reverseChargeCode].filter((code) => ['81', '83', '86', '88'].includes(String(code)));
  if (reverseCodes.length) return Array.from(new Set(reverseCodes));
  const lines = safeArray(expense.purchaseLines).length ? safeArray(expense.purchaseLines) : [expense];
  return Array.from(new Set(lines.map((line) => {
    const explicit = String(line.standardVatCode || line.purchaseVatCode || '');
    if (explicit) return explicit === '23' ? '20' : explicit;
    return ['zeroRated', 'exempt', 'importBasis'].includes(line.vatTreatment) || num(line.vatRate) === 0 ? '0' : num(line.vatRate) === 15 ? '11' : num(line.vatRate) === 12 ? '13' : '1';
  })));
};

const voucherAttachments = (entry) => {
  const attachments = safeArray(entry?.attachments).map((attachment) => stripUndefinedDeep({
    id: attachment.id || attachment.attachmentId || attachment.storagePath || attachment.name,
    name: attachment.name || attachment.attachmentName || entry?.attachmentName || entry?.fileName || 'Bilag',
    mimeType: attachment.mimeType || attachment.type || entry?.attachmentMimeType || '',
    storagePath: attachment.storagePath || '',
    downloadUrl: attachment.downloadUrl || attachment.file || '',
  }));
  const fallbackUrl = entry?.receiptFile || entry?.file || entry?.downloadUrl || '';
  if (fallbackUrl || entry?.attachmentId) {
    attachments.push(stripUndefinedDeep({
      id: entry.attachmentId || entry.id,
      name: entry.attachmentName || entry.fileName || entry.supplierInvoiceNumber || entry.voucherNumber || 'Bilag',
      mimeType: entry.attachmentMimeType || '',
      storagePath: entry.attachmentStoragePath || '',
      downloadUrl: fallbackUrl,
    }));
  }
  return Array.from(new Map(attachments.map((attachment) => [
    attachment.downloadUrl || attachment.storagePath || attachment.id || attachment.name,
    attachment,
  ])).values());
};

const deriveVouchers = (state, options = {}) => {
  const country = String(state.settings?.country || 'NO').toUpperCase();
  const invoiceVouchers = safeArray(state.invoices)
    .filter((invoice) => isPostedInvoice(invoice, state))
    .map((invoice) => ({
      id: invoice.id,
      sourceType: 'invoice',
      sourceId: invoice.id,
      kind:
        invoice.kind === 'credit'
          ? 'kreditnota'
          : invoice.kind === 'offer'
            ? 'tilbud'
            : invoice.kind === 'order_confirmation'
              ? 'ordrebekreftelse'
              : 'salgsfaktura',
      num: invoice.invoiceNumber || invoice.id,
      name: invoice.customer?.name || 'Uten kunde',
      date: isoToNb(invoice.date),
      net: voucherMoney(invoiceNet(invoice)),
      gross: voucherMoney(invoiceGross(invoice)),
      status: invoice.status || 'active',
      paymentStatus: dashboardInvoicePaymentSummary(state, invoice).status,
      hasAttachment: false, attachments: voucherAttachments(invoice),
      vatReportCodes: invoiceVatReportCodes(invoice, country),
      source: invoice,
      postings: [
        ...invoicePostings(invoice),
        ...safeArray(state.payments)
          .filter((payment) => payment.documentType === 'invoice' && payment.documentId === invoice.id && payment.status !== 'reversed')
          .flatMap(trialPaymentPostings),
      ],
    }));
  const purchaseVouchers = safeArray(state.expenses).filter(isActiveExpense).map((expense) => {
    const breakdown = dashboardExpenseVatBreakdown(expense);
    const net = breakdown.costAmount;
    const gross = breakdown.grossAmount;
    const isFreePosting = isFreePostingExpense(expense);
    const isMileage = isMileageExpense(expense);
    const supplierPaymentSummary = expense.isSupplierInvoice ? dashboardSupplierInvoicePaymentSummary(state, expense) : null;
    const supplierPaymentStatus = supplierPaymentSummary?.status || null;
    const supplierKey = (entry) => String(entry?.supplierId || entry?.supplierOrganizationNumber || entry?.supplierName || '').trim().toLowerCase();
    return {
      id: expense.id,
      sourceType: 'expense',
      sourceId: expense.id,
      kind: isMileage ? 'kjorebok' : expense.isSupplierInvoice ? 'leverandorfaktura' : isFreePosting ? 'manuelt' : 'kjop',
      num: expense.supplierInvoiceNumber || expense.voucherNumber || expense.id,
      name: expense.supplierName || expense.description || 'Kj\u00f8p',
      date: isoToNb(expense.date),
      net: -voucherMoney(net),
      gross: -voucherMoney(gross),
      status: expense.status || 'active',
      paymentStatus: supplierPaymentStatus,
      paidAmount: supplierPaymentSummary?.paidAmount || 0,
      remainingAmount: supplierPaymentSummary?.remainingAmount || 0,
      overpaidAmount: supplierPaymentSummary?.overpaidAmount || 0,
      payments: expense.isSupplierInvoice ? activePaymentsForDocument(state, 'supplier_invoice', expense.id).map(mapInvoicePaymentToUi) : [],
      supplierOverpaymentAllocations: expense.isSupplierInvoice ? safeArray(state.supplierSettlements)
        .filter((entry) => entry.status !== 'reversed' && entry.kind === 'overpayment_offset' && (entry.expenseId === expense.id || entry.sourceExpenseId === expense.id))
        .map((entry) => ({ ...entry, amount: backendRoundMoney(entry.amount) })) : [],
      availableSupplierOverpayments: expense.isSupplierInvoice ? safeArray(state.expenses)
        .filter((entry) => entry.id !== expense.id && entry.isSupplierInvoice && isActiveExpense(entry) && supplierKey(entry) && supplierKey(entry) === supplierKey(expense))
        .map((entry) => ({ expense: entry, summary: dashboardSupplierInvoicePaymentSummary(state, entry) }))
        .filter(({ summary }) => summary.overpaidAmount > 0.005)
        .map(({ expense: source, summary }) => ({ id: source.id, num: source.supplierInvoiceNumber || source.voucherNumber || source.id, amount: backendRoundMoney(summary.overpaidAmount) })) : [],
      hasAttachment: hasPrimaryAttachment(expense),
      attachmentStatus: expense.attachmentStatus,
      attachmentMissingReason: expense.attachmentMissingReason,
      attachmentMissingMarkedAt: expense.attachmentMissingMarkedAt,
      attachments: voucherAttachments(expense),
      vatReportCodes: expenseVatReportCodes(expense, country),
      source: expense,
      postings: [
        ...trialExpensePostings(expense),
        ...(expense.isSupplierInvoice
          ? safeArray(state.payments)
              .filter((payment) => payment.documentType === 'supplier_invoice' && payment.documentId === expense.id && payment.status !== 'reversed')
              .flatMap(trialPaymentPostings)
          : [directPurchasePaymentEvent(expense)].filter(Boolean).flatMap(trialPaymentPostings)),
      ],
    };
  });
  const otherSales = safeArray(state.otherSales).filter(isActiveOtherSale).map((sale) => ({
    id: sale.id,
    sourceType: 'otherSale',
    sourceId: sale.id,
    kind: 'annetsalg',
    num: otherSaleVoucherNumber(sale),
    name: sale.description || 'Annet salg',
    date: isoToNb(sale.date),
    net: voucherMoney(sale.amountNet),
    gross: globalThis.PlummeOtherSaleAmounts.totals(sale).gross,
    status: sale.status || 'active',
    hasAttachment: hasPrimaryAttachment(sale),
    attachments: voucherAttachments(sale),
    vatReportCodes: otherSaleVatReportCodes(sale, country),
    source: sale,
    postings: otherSalePostings(sale, country),
  }));
  const customsDeclarations = safeArray(state.customsDeclarations).filter((entry) => options.includeAnnulled || entry.status !== 'annulled').map((entry) => ({
    id: entry.id,
    sourceType: 'customsDeclaration',
    sourceId: entry.id,
    kind: 'tolldeklarasjon',
    num: entry.declarationReference || entry.id,
    name: entry.description || 'Tolldeklarasjon',
    date: isoToNb(entry.date),
    net: voucherMoney(entry.customsBasis),
    gross: voucherMoney(entry.customsBasis),
    status: entry.status || 'active',
    hasAttachment: hasPrimaryAttachment(entry),
    attachments: voucherAttachments(entry),
    vatReportCodes: [String(entry.customsVatCode || '81')],
    source: entry,
    postings: customsDeclarationPostings(entry),
  }));
  return [...invoiceVouchers, ...purchaseVouchers, ...otherSales, ...customsDeclarations]
    .sort((a, b) => nbToIso(b.date).localeCompare(nbToIso(a.date)));
};

// SAF-T og komplett regnskapseksport skal inneholde selvstendige journalhendelser
// med hendelsens egen dato. UI-bilagslisten kan fortsatt gruppere betalinger under
// dokumentet, men eksporten må ikke flytte betalingsdatoen til fakturadatoen.
// Built once per events array and settings object (speed track S0): the trial balance,
// result, chart, VAT and voucher views all asked for the same derivation, several times per
// report. Callers receive a fresh array so sorting or filtering never reaches the cache.
const journalVouchersCache = new WeakMap();
const accountingJournalVouchers = (state) => {
  const events = safeArray(state.accountingEvents);
  // The journal is append-only and the app replaces the array on every update, but tests
  // and tools may push onto it in place; the length and last event are part of the key.
  const last = events[events.length - 1];
  const cached = journalVouchersCache.get(events);
  if (cached && cached.settings === state.settings && cached.length === events.length && cached.last === last) return cached.vouchers.slice();
  const vouchers = buildAccountingJournalVouchers(state, events);
  if (Array.isArray(state.accountingEvents)) journalVouchersCache.set(events, { settings: state.settings, length: events.length, last, vouchers });
  return vouchers.slice();
};
const buildAccountingJournalVouchers = (state, events) => {
  const reversedByEventId = new Map(events
    .filter((event) => event?.reverses_event_id)
    .map((event) => [String(event.reverses_event_id), String(event.event_id || '')]));
  const eventById = new Map(events.map((event) => [String(event?.event_id || ''), event]));
  return events.map((event) => {
    const refs = event.document_references || {};
    const reportDimensions = (event.reverses_event_id ? eventById.get(String(event.reverses_event_id)) : event)?.report_dimensions;
    const specifications = new Map(safeArray(event.vat_specification).map((spec) => [String(spec.vat_spec_id || ''), spec]));
    // Customs has no purchase base line. Carry each recorded base once on the
    // output VAT line (or its basis line for code 85), including reversals.
    const customsBasisLines = new Map();
    if (/^customsDeclaration(?:Reversal)?$/.test(event.source_type)) specifications.forEach((spec, id) => {
      const linked = safeArray(event.posting_lines).filter((line) => safeArray(line.vat_spec_ids).includes(id));
      customsBasisLines.set(id, (linked.find((line) => /^270[0-3]$/.test(line.account)) || linked[0])?.line_id);
    });
    const postings = safeArray(event.posting_lines).map((line) => {
      const linked = safeArray(line.vat_spec_ids).map((id) => specifications.get(String(id))).filter(Boolean);
      const spec = linked[0];
      const code = String(line.account || '').trim();
      const isVat = /^27(0[0-3]|1[0-3])$/.test(code);
      const dimensionLine = safeArray(reportDimensions?.purchaseLines).find((entry) => entry.id === spec?.source_line_id);
      return posting(code, plumme1AccountName(code, line.description || `Konto ${code}`, state.settings), line.description || refs.document_number || event.source_type, line.signed_amount, { ...(line.asset_id?{assetId:line.asset_id}:{}), ...(line.party_type === 'SUPPLIER' && line.party_id ? { supplierId: line.party_id } : {}), ...(reportDimensions ? { reportDimensions: dimensionLine || { projectId: reportDimensions.projectId, departmentId: reportDimensions.departmentId } } : {}), ...(spec ? {
        vatSpecificationIds: linked.map((entry) => entry.vat_spec_id),
        saftTaxInformation: {
          code: spec.standard_tax_code,
          rate: num(spec.rate),
          base: customsBasisLines.has(String(spec.vat_spec_id))
            ? customsBasisLines.get(String(spec.vat_spec_id)) === line.line_id ? Math.abs(num(spec.base_amount)) : 0
            : isVat ? 0 : num(spec.base_amount),
          amount: isVat ? Math.abs(num(line.signed_amount)) : 0,
          side: num(line.signed_amount) >= 0 ? 'debit' : 'credit',
        },
      } : {}) });
    });
    const displayAmounts = window.PlummeTransactionVoucherModel.journalAmounts(event);
    return {
      id: event.event_id,
      eventId: event.event_id,
      recordedSeq: event.recorded_seq,
      recordedOn: event.recorded_at_utc,
      sourceType: event.source_type,
      sourceId: event.source_id,
      kind: event.reverses_event_id ? 'reversal' : refs.document_kind || event.source_type,
      num: String(event.recorded_seq || refs.document_number || event.source_id),
      name: refs.document_number || event.source_type,
      date: isoToNb(event.accounting_date),
      ...displayAmounts,
      status: 'active',
      hasAttachment: false, // document_id identifies the source document, not an uploaded attachment.
      source: { id: event.source_id, ...refs, ...(reportDimensions || {}) },
      reversesEntryId: event.reverses_event_id || undefined,
      reversedByEntryId: reversedByEventId.get(String(event.event_id || '')) || undefined,
      replacesEntryId: event.replaces_event_id || undefined, vatReportCodes: Array.from(new Set(safeArray(event.vat_specification).map((spec) => String(spec.standard_tax_code || '').trim()).filter(Boolean))),
      vatSpecification: safeArray(event.vat_specification),
      postings,
    };
  })
  .filter((voucher) => voucher.postings.length)
  .sort((left, right) => num(right.recordedSeq) - num(left.recordedSeq));
};

const deriveAccountingLedgerVouchers = (state) => {
  // Presence of the field is deliberate: an empty or incomplete journal must not
  // silently fall back to a projection from mutable source documents.
  if (Array.isArray(state?.accountingEvents)) return accountingJournalVouchers(state);
  const sourceVouchers = deriveVouchers(state).map((voucher) => {
    if (voucher.sourceType === 'invoice') return { ...voucher, postings: invoicePostings(voucher.source) };
    if (voucher.sourceType === 'expense') return { ...voucher, postings: trialExpensePostings(voucher.source) };
    return voucher;
  });
  const paymentVouchers = safeArray(state.payments)
    .filter((payment) => payment?.status !== 'reversed' && payment?.settlementKind !== 'no_payment')
    .map((payment) => {
      const outgoing = payment.direction === 'outgoing';
      const feeAmount = Math.max(0, num(payment.feeAmount));
      const bankAmount = payment.bankAmount === undefined || payment.bankAmount === null || payment.bankAmount === ''
        ? (outgoing ? num(payment.amount) + feeAmount : num(payment.amount) - feeAmount)
        : num(payment.bankAmount);
      const signedBankAmount = outgoing ? -Math.abs(bankAmount) : Math.abs(bankAmount);
      return {
        id: payment.id,
        sourceType: 'payment',
        sourceId: payment.id,
        kind: 'betaling',
        num: payment.voucherNumber || payment.paymentNumber || payment.id,
        name: payment.reference || (outgoing ? 'Betaling' : 'Innbetaling'),
        date: isoToNb(payment.date),
        net: signedBankAmount,
        gross: signedBankAmount,
        status: payment.status || 'active',
        hasAttachment: false,
        source: payment,
        postings: trialPaymentPostings(payment),
      };
    });
  const reversedPaymentHistoryVouchers = safeArray(state.payments)
    .filter((payment) => {
      if (payment?.status !== 'reversed' || payment?.settlementKind === 'no_payment') return false;
      if (payment.documentType !== 'supplier_invoice') return true;
      return !safeArray(state.expenses).some((expense) => expense.id === payment.documentId && expense.status === 'annulled');
    })
    .flatMap((payment) => {
      // Legacy replacements did not persist an accounting reversal date. The
      // linked replacement still proves it, while `reversedAt` is only the
      // immutable audit timestamp for when the edit was performed.
      const replacement = payment.replacedByPaymentId
        ? safeArray(state.payments).find((entry) => entry?.id === payment.replacedByPaymentId)
        : null;
      return historyVoucherPair({
      id: payment.id,
      sourceType: 'paymentHistory',
      sourceId: payment.id,
      kind: 'betaling',
      number: payment.voucherNumber || payment.paymentNumber || payment.id,
      name: payment.reference || (payment.direction === 'outgoing' ? 'Betaling' : 'Innbetaling'),
      date: payment.date,
      reversedAt: globalThis.PlummeAccountingScope.accountingEventDate(state, payment.reversalDate || replacement?.date || payment.reversedAt || payment.date),
      postings: trialPaymentPostings({ ...payment, status: 'active' }),
      });
    });
  const directPurchasePaymentVouchers = safeArray(state.expenses)
    .map(directPurchasePaymentEvent)
    .filter(Boolean)
    .map((payment) => ({
      id: payment.id,
      sourceType: 'expensePayment',
      sourceId: payment.documentId,
      kind: 'betaling',
      num: payment.voucherNumber || payment.id,
      name: payment.reference || 'Betaling av kj\u00f8p',
      date: isoToNb(payment.date),
      net: -payment.amount,
      gross: -payment.amount,
      status: 'active',
      hasAttachment: payment.hasAttachment === true,
      attachments: voucherAttachments(payment),
      source: payment,
      postings: trialPaymentPostings(payment),
    }));
  const purchaseHistoryVouchers = annulledPurchaseAccountingHistory(state);
  const customsHistoryVouchers = annulledCustomsAccountingHistory(state);
  const payrollVouchers = safeArray(state.payrollRuns)
    .filter((run) => ['approved', 'paid'].includes(run?.status))
    .map((run) => ({
      id: run.id,
      sourceType: 'payroll',
      sourceId: run.id,
      kind: 'lønn',
      num: payrollRunVoucherNumber(run, state.payrollRuns),
      name: `Lønn ${run.period || ''}`.trim(),
      date: isoToNb(payrollRunPostingDate(run)),
      status: run.status,
      payrollRunId: run.id,
      payrollPeriod: run.period,
      payrollTerm: payrollTaxPaymentTerm({ date: run.paymentDate || payrollRunPostingDate(run) }),
      postings: payrollRunPostings(run, state),
    }));
  const payrollPaymentVouchers = safeArray(state.payrollRuns).flatMap((run) =>
    payrollRunPaymentEvents(run, state).map((event) => ({
      id: event.id,
      sourceType: 'payrollPayment',
      sourceId: run.id,
      kind: 'lønnsbetaling',
      num: event.voucherNumber || event.id,
      name: event.description,
      date: isoToNb(event.date),
      status: 'active',
      payrollRunId: run.id,
      payrollPeriod: run.period,
      payrollTerm: event.payrollTerm || payrollTaxPaymentTerm({ date: event.date }),
      postings: event.postings,
    })),
  );
  const payrollHistoryVouchers = safeArray(state.payrollRuns)
    .flatMap((run) => payrollAccountingHistoryVouchers(run, state));
  const bankVouchers = safeArray(state.bankReconciliationPostings)
    .filter((entry) => entry?.status !== 'annulled')
    .map((entry) => ({
      id: entry.id,
      sourceType: 'bankPosting',
      sourceId: entry.id,
      kind: 'bankpostering',
      num: entry.voucherNumber || bankPostingVoucherNumber(entry.id),
      name: entry.description || 'Bankpostering',
      date: isoToNb(entry.date),
      net: bankPostingBankDirectionAmount(entry),
      gross: bankPostingBankDirectionAmount(entry),
      status: entry.status || 'active',
      hasAttachment: Boolean(entry.attachmentName || entry.attachmentId),
      postings: bankReconciliationPostingLines(entry),
    }));
  const yearEndVouchers = Object.entries(state.yearEndEntriesByYear || {}).flatMap(([year, entries]) =>
    safeArray(entries).filter((entry) => entry?.status !== 'annulled').map((entry) => ({
      id: entry.id,
      sourceType: 'yearEndEntry',
      sourceId: entry.id,
      kind: entry.kind || 'årsoppgjør',
      num: entry.id,
      name: entry.description || entry.kind || `Årsoppgjør ${year}`,
      date: isoToNb(entry.date || `${year}-12-31`),
      status: entry.status || 'active',
      postings: safeArray(entry.lines).map((line) => posting(line.accountCode, line.description || plumme1AccountName(line.accountCode, `Konto ${line.accountCode}`), entry.description || 'Årsoppgjør', num(line.signedAmount),line.assetId?{assetId:line.assetId}:{})),
    })));
  const payrollTaxVouchers = Object.values(state.payrollTaxPaymentsByYear || {}).flatMap((entries) =>
    safeArray(entries).filter((entry) => entry?.status !== 'reversed').map((entry) => ({
      id: entry.id,
      sourceType: 'payrollTaxPayment',
      sourceId: entry.id,
      kind: 'betaling-offentlige-avgifter',
      num: payrollTaxPaymentVoucherNumber(entry, entries),
      name: entry.note || 'Betaling forskuddstrekk og arbeidsgiveravgift',
      date: isoToNb(entry.date),
      status: entry.status || 'active',
      payrollRunId: entry.runId,
      payrollTerm: entry.term || payrollTaxPaymentTerm(entry),
      reportingYear: entry.reportingYear || entry.year,
      postings: payrollTaxPaymentPostings(state, entry),
    })));
  const mileageVouchers = safeArray(state.mileageEntries).filter(entry => entry.workflowVersion !== 2 || entry.workflowStatus === 'posted')
    .filter((entry) => {
      const year = Number(String(entry?.date || '').slice(0, 4)) || state.activeYear || new Date().getFullYear();
      const scope = window.PlummeAccountingScope?.resolveAccountingScope?.(state.settings || {}, year);
      return scope?.jurisdiction === 'NO'
        && scope?.entityFamily === 'sole_trader'
        && entry?.status !== 'annulled'
        && entry?.status !== 'annullert'
        && entry?.usageType !== 'private'
        && entry?.privateUse !== true
        && num(entry?.amountNet) > 0.005;
    })
    .map((entry) => {
      const amount = voucherMoney(entry.amountNet);
      return {
        id: entry.id,
        sourceType: 'mileage',
        sourceId: entry.id,
        kind: 'kjorebok',
        num: entry.voucherNumber || entry.id,
        name: entry.purpose || `${entry.fromLocation || ''} – ${entry.toLocation || ''}`.trim() || 'Yrkesreise med privat bil',
        date: isoToNb(entry.date),
        net: amount,
        gross: amount,
        status: entry.status || 'active',
        hasAttachment: false,
        source: entry,
        postings: [
          posting('7100', plumme1AccountName('7100', 'Bilgodtgjørelse'), 'Yrkesreise med privat bil', amount),
          posting('2060', plumme1AccountName('2060', 'Privatkonto'), 'Motkonto yrkesreise med privat bil', -amount),
        ],
      };
    });
  return [...sourceVouchers, ...paymentVouchers, ...reversedPaymentHistoryVouchers, ...directPurchasePaymentVouchers, ...purchaseHistoryVouchers, ...customsHistoryVouchers, ...additionalReversalVouchers(state), ...payrollVouchers, ...payrollPaymentVouchers, ...payrollHistoryVouchers, ...bankVouchers, ...fixedAssetDisposalVouchers(state), ...yearEndVouchers, ...payrollTaxVouchers, ...mileageVouchers]
    .filter((voucher) => safeArray(voucher.postings).length)
    .map((voucher) => {
      if (voucher.gross !== undefined && voucher.net !== undefined) return voucher;
      const debit = safeArray(voucher.postings).reduce((sum, line) => sum + Math.max(0, num(line.amount)), 0);
      const credit = safeArray(voucher.postings).reduce((sum, line) => sum + Math.max(0, -num(line.amount)), 0);
      const gross = debit || credit;
      return { ...voucher, net: gross, gross, hasAttachment: Boolean(voucher.hasAttachment) };
    })
    .sort((a, b) => nbToIso(b.date).localeCompare(nbToIso(a.date)));
};

const importedVatCodeDirection = (code) => {
  const value = String(code || '').trim();
  if (/^(1|8)/.test(value)) return 'input';
  return 'output';
};

const importedVatSummary = (state, year, options = {}) => {
  const stats = state.accountingImportReportStatsByYear?.[String(year)] || {};
  const vat = stats.vat || {};
  const monthStart = Array.isArray(options.months) ? Number(options.months[0]) + 1 : 1;
  const monthEnd = Array.isArray(options.months) ? Number(options.months[1]) + 1 : 12;
  const monthly = safeArray(vat.monthly).filter((row) => Number(row.month) >= monthStart && Number(row.month) <= monthEnd);
  const filings = safeArray(vat.filings).filter((row) => Number(row.periodEndMonth) >= monthStart && Number(row.periodEndMonth) <= monthEnd);
  const hasFilingEvidence = filings.length > 0;
  const outgoing = hasFilingEvidence
    ? filings.reduce((sum, row) => sum + num(row.outputVat), 0)
    : monthly.reduce((sum, row) => sum + num(row.outputVat), 0);
  const incoming = hasFilingEvidence
    ? filings.reduce((sum, row) => sum + num(row.inputVat), 0)
    : monthly.reduce((sum, row) => sum + num(row.inputVat), 0);
  const filingNetVat = filings.reduce((sum, row) => sum + num(row.netVat), 0);
  const codeMap = new Map();
  safeArray(vat.codes)
    .filter((row) => Number(row.month) >= monthStart && Number(row.month) <= monthEnd)
    .forEach((row) => {
      const code = String(row.code || 'import').trim() || 'import';
      const key = `${code}:${num(row.rate)}`;
      const current = codeMap.get(key) || {
        code,
        desc: `Importert SAF-T MVA-kode ${code}`,
        grunnlag: 0,
        sats: num(row.rate),
        mva: 0,
        hint: 'Importert fra historisk SAF-T. Dette er arkivgrunnlag, ikke en ny bokføring i Plumme.',
        kind: 'Importert historikk',
      };
      current.grunnlag += num(row.base);
      current.mva += Math.abs(num(row.taxAmount)) * (importedVatCodeDirection(code) === 'input' ? -1 : 1);
      codeMap.set(key, current);
    });
  if (!codeMap.size && (outgoing > 0.005 || incoming > 0.005)) {
    if (outgoing > 0.005) {
      codeMap.set('import-output', {
        code: 'import-utg',
        desc: 'Importert utgående MVA fra 270x-kontoer',
        grunnlag: 0,
        sats: 0,
        mva: Math.round(outgoing),
        hint: 'SAF-T hadde MVA-saldo på 270x, men ikke detaljert MVA-kodegrunnlag for denne terminen.',
        kind: 'Importert historikk',
      });
    }
    if (incoming > 0.005) {
      codeMap.set('import-input', {
        code: 'import-inng',
        desc: 'Importert inngående MVA fra 271x-kontoer',
        grunnlag: 0,
        sats: 0,
        mva: -Math.round(incoming),
        hint: 'SAF-T hadde MVA-saldo på 271x, men ikke detaljert MVA-kodegrunnlag for denne terminen.',
        kind: 'Importert historikk',
      });
    }
  }
  return {
    outgoing,
    incoming,
    settlementMovement: monthly.reduce((sum, row) => sum + num(row.settlementMovement), 0),
    filingNetVat,
    hasFilingEvidence,
    hasData: monthly.length > 0 || codeMap.size > 0 || hasFilingEvidence,
    codes: [...codeMap.values()].map((row) => ({
      ...row,
      grunnlag: Math.round(row.grunnlag),
      mva: Math.round(row.mva),
    })),
    source: vat.source || '',
  };
};

const startupVatSummary = (state, year, options = {}) => {
  if (state.accountingImportReportStatsByYear?.[String(year)]) return { outgoing: 0, incoming: 0, codes: [] };
  const opening = state.openingBalanceByYear?.[String(year)] || {};
  const periodEndMonth = Number(opening.vatCarryoverPeriodEndMonth);
  if (opening.vatCarryoverEnabled !== true || !Number.isInteger(periodEndMonth) || periodEndMonth < 1 || periodEndMonth > 12) {
    return { outgoing: 0, incoming: 0, codes: [] };
  }
  const day = String(new Date(year, periodEndMonth, 0).getDate()).padStart(2, '0');
  const targetDate = `${year}-${String(periodEndMonth).padStart(2, '0')}-${day}`;
  if (!reportDateInRange(targetDate, year, options)) return { outgoing: 0, incoming: 0, codes: [] };
  const outgoing = Math.max(0, num(opening.outgoingVat));
  const incoming = Math.max(0, num(opening.incomingVat));
  return {
    outgoing,
    incoming,
    codes: [
      outgoing > 0.005 ? {
        code: 'oppstart-utg',
        desc: 'Utgående MVA fra oppstartstall',
        grunnlag: 0,
        sats: 0,
        mva: Math.round(outgoing),
        hint: 'Ikke rapportert MVA fra tidligere system som er valgt med i denne terminen.',
        kind: 'Oppstartstall',
      } : null,
      incoming > 0.005 ? {
        code: 'oppstart-inng',
        desc: 'Inngående MVA fra oppstartstall',
        grunnlag: 0,
        sats: 0,
        mva: -Math.round(incoming),
        hint: 'Ikke rapportert MVA fra tidligere system som er valgt med i denne terminen.',
        kind: 'Oppstartstall',
      } : null,
    ].filter(Boolean),
  };
};

const VAT_MONTH_LABELS = [
  'januar',
  'februar',
  'mars',
  'april',
  'mai',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'desember',
];
const VAT_SHORT_MONTH_LABELS = ['jan.', 'feb.', 'mars', 'apr.', 'mai', 'juni', 'juli', 'aug.', 'sep.', 'okt.', 'nov.', 'des.'];
const vatPeriodMonthSpan = (vatPeriod) => ({
  monthly: 1,
  bimonthly: 2,
  quarterly: 3,
  semiannual: 6,
}[vatPeriod] || 12);
const vatTermCount = (vatPeriod) => Math.ceil(12 / vatPeriodMonthSpan(vatPeriod));
const vatTermRange = (year, termIndex, vatPeriod) => {
  const span = vatPeriodMonthSpan(vatPeriod);
  const monthStart = termIndex * span;
  const monthEnd = Math.min(11, monthStart + span - 1);
  return {
    monthStart,
    monthEnd,
    dateFrom: `${year}-${String(monthStart + 1).padStart(2, '0')}-01`,
    dateTo: new Date(Date.UTC(year, monthEnd + 1, 0)).toISOString().slice(0, 10),
  };
};
const vatDueDate = (year, termIndex, vatPeriod) => {
  if (vatPeriod === 'annual') return new Date(year + 1, 2, 10);
  if (vatPeriod === 'bimonthly') {
    return [
      new Date(year, 3, 10),
      new Date(year, 5, 10),
      new Date(year, 7, 31),
      new Date(year, 9, 10),
      new Date(year, 11, 10),
      new Date(year + 1, 1, 10),
    ][termIndex];
  }
  const range = vatTermRange(year, termIndex, vatPeriod);
  const due = new Date(year, range.monthEnd + 2, 10);
  return Number.isNaN(due.getTime()) ? new Date(year + 1, 2, 10) : due;
};
const formatVatDate = (date, long = false) => {
  const months = long ? VAT_MONTH_LABELS : VAT_SHORT_MONTH_LABELS;
  return `${date.getDate()}. ${months[date.getMonth()]} ${date.getFullYear()}`;
};
const vatTermStatusValue = (deadlineStatus, key, legacyKey) => ({
  submitted: Boolean(deadlineStatus.mvaTerms?.[key] || deadlineStatus.mvaTerms?.[legacyKey]),
  paid: Boolean(deadlineStatus.mvaPaidTerms?.[key] || deadlineStatus.mvaPaidTerms?.[legacyKey]),
  payments: [
    ...safeArray(deadlineStatus.mvaPaymentRecords?.[key]),
    ...(legacyKey === key ? [] : safeArray(deadlineStatus.mvaPaymentRecords?.[legacyKey])),
  ],
});
const vatSettlementNetAmount = (payments, expectedSettlementKind = 'payment') =>
  safeArray(payments).reduce((sum, record) => {
    const amount = Math.abs(num(record.amount));
    const kind = record.settlementKind || expectedSettlementKind;
    if (expectedSettlementKind === 'refund') return sum + (kind === 'refund' ? amount : -amount);
    return sum + (kind === 'payment' ? amount : -amount);
  }, 0);
const buildVatFilingModel = (options = {}) => {
  if (!runtime.appState || (!runtime.configured && !runtime.user)) return null;
  const state = mergeState(runtime.appState);
  const year = Number(options.year || state.activeYear) || new Date().getFullYear();
  if (Array.isArray(state.accountingEvents) && !(window.PlummeAccountingBasisCoverage.isLoading(state.accountingBasisCoverage) && ledgerBalancesFor(state, year, {})) && window.PlummeAccountingBasisCoverage.isBlocked(state.accountingBasisCoverage, { allowImportedStatistics: true, period: { from: `${year}-01-01`, to: `${year}-12-31` } })) return window.PlummeAccountingBasisCoverage.blockedVat(year, state.accountingBasisCoverage, state, mapPaymentAccountsToUi(state));
  const filingCountry = String(state.settings?.countryCode || state.settings?.country || state.settings?.businessCountry || '').toUpperCase();
  if (filingCountry && filingCountry !== 'NO') return { year, terms: [], totals: { toPay: 0, paid: 0, receivable: 0, refunded: 0 }, vatRegistered: false, reviewRequired: true };
  const vatPeriod = window.PlummeVatPeriodHistory?.forDate?.(state.settings, `${year}-01-01`) || state.settings?.vatPeriod || 'bimonthly';
  const deadlineStatus = state.deadlinesByYear?.[String(year)] || {};
  const today = new Date();
  const termTotal = vatPeriod === 'annual' ? 1 : vatTermCount(vatPeriod);
  const terms = Array.from({ length: termTotal }, (_, index) => {
    const key = vatPeriod === 'annual' ? 'mvaSubmitted' : `term-${index + 1}`;
    const legacyKey = vatPeriod === 'annual' ? key : `${year}-${index + 1}`;
    const range = vatTermRange(year, index, vatPeriod);
    const dueDate = vatDueDate(year, index, vatPeriod);
    const importedTermVat = importedVatSummary(state, year, { months: [range.monthStart, range.monthEnd] });
    const summary = deriveVat(state, year, { months: [range.monthStart, range.monthEnd] });
    const termStatus = vatPeriod === 'annual'
      ? {
          submitted: Boolean(deadlineStatus.mvaSubmitted),
          paid: Boolean(deadlineStatus.mvaPaid),
          payments: safeArray(deadlineStatus.mvaPaymentRecords?.[key] || deadlineStatus.mvaPaymentRecords?.annual),
        }
      : vatTermStatusValue(deadlineStatus, key, legacyKey);
    const netVat = summary.netVat ?? summary.utgaaende - summary.inngaaende;
    const expectedSettlementKind = netVat < -0.005 ? 'refund' : 'payment';
    const paidAmount = vatSettlementNetAmount(termStatus.payments, expectedSettlementKind);
    const expectedSettlement = Math.abs(Math.round(netVat));
    const paidInFull = expectedSettlement <= 0.005 || paidAmount + 0.005 >= expectedSettlement;
    const periodStarted = new Date(`${range.dateFrom}T12:00:00`) <= today;
    const importedArchiveOnly = Boolean(summary.importedArchiveOnly) || (importedTermVat.hasData && !termStatus.submitted);
    const status = importedArchiveOnly
      ? 'arkiv'
      : termStatus.submitted
      ? (paidInFull ? 'levert-betalt' : 'levert')
      : (periodStarted ? 'ikke-levert' : 'kommende');
    const kind = Math.abs(netVat) <= 0.005
      ? 'ingen'
      : netVat < 0
        ? 'tilgode'
        : paidInFull
          ? 'betalt'
          : 'abetale';
    const amount = expectedSettlement;
    const declarations = safeArray(state.customsDeclarations)
      .filter((entry) => reportDateInRange(entry.date, year, { months: [range.monthStart, range.monthEnd] }));
    return {
      n: index + 1,
      key,
      legacyKey,
      year,
      months: [range.monthStart, range.monthEnd],
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      period: range.monthStart === range.monthEnd
        ? VAT_MONTH_LABELS[range.monthStart]
        : `${VAT_MONTH_LABELS[range.monthStart]} - ${VAT_MONTH_LABELS[range.monthEnd]}`,
      frist: formatVatDate(dueDate),
      fristLong: formatVatDate(dueDate, true),
      dueDate: dueDate.toISOString(),
      status,
      importedArchiveOnly,
      importedArchiveSource: importedTermVat.source,
      importedFilingEvidence: importedTermVat.hasFilingEvidence,
      amount,
      kind,
      utgaaende: summary.utgaaende,
      inngaaende: summary.inngaaende,
      tilgode: Math.max(0, -Math.round(netVat)),
      skyldig: Math.max(0, Math.round(netVat)),
      netVat,
      betalt: paidAmount,
      settlementKind: expectedSettlementKind,
      codes: summary.codes,
      declarations,
      payments: termStatus.payments,
    };
  });
  const totals = terms.reduce((sum, term) => {
    const paid = term.betalt || 0;
    if (term.netVat > 0) {
      sum.toPay += Math.max(0, Math.round(term.netVat) - paid);
      sum.paid += Math.max(0, paid);
    } else if (term.netVat < 0) {
      sum.receivable += Math.max(0, Math.abs(Math.round(term.netVat)) - paid);
      sum.refunded += Math.max(0, paid);
    }
    return sum;
  }, { toPay: 0, paid: 0, receivable: 0, refunded: 0 });
  return { year, vatPeriod, terms, totals, importedArchiveOnly: terms.length > 0 && terms.every(term => term.importedArchiveOnly), accountingBasis: state.accountingBasisCoverage, vatRegistered: Boolean(state.settings?.vatRegistered), paymentAccounts: mapPaymentAccountsToUi(state) };
};
